import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { PlatformIcon, PLATFORM_COLORS } from '../components/shared/PlatformIcon';
import { ContactLabelSelector } from '../components/ContactLabelSelector';
import { useAuth } from '../contexts/AuthContext';
import { useChatNotifications } from '../contexts/ChatNotificationsContext';
import { useTenants } from '../hooks/useTenants';
import { useCreditConfirm } from '../components/credits/useCreditConfirm';
import { CreditCostNotice } from '../components/credits/CreditCostNotice';
import { VoiceCloneManagerModal, VoiceMessageModal, fetchVoiceClones, type VoiceClone, type GeneratedVoiceAudio } from '../components/crm/VoiceCloneModals';
import { ScheduleMeetingModal, meetingFetch, MEETING_STATUS_LABELS, type ExistingMeeting } from '../components/crm/ScheduleMeetingModal';
import type { Funnel } from '../types';
import { WahaHealthAlert } from '../components/whatsapp/WahaHealthAlert';
import type { WahaSessionHealth } from '../types/wahaHealth';

type Inbox = {
  id: string;
  provider: string;
  platform: string;
  channelType: string;
  externalAccountId?: string;
  externalThreadId?: string;
  externalContactId?: string;
  title?: string;
  contactName?: string;
  contactHandle?: string;
  metadata?: Record<string, unknown>;
  connectionHealth?: WahaSessionHealth | null;
};

type Conversation = {
  id: string;
  provider: string;
  platform: string;
  channelType: string;
  externalAccountId?: string;
  externalThreadId?: string;
  externalContactId?: string;
  contactId?: string;
  contactName?: string;
  contactHandle?: string;
  contactPhone?: string;
  groupName?: string;
  title?: string;
  metadata?: Record<string, unknown>;
  lastMessageAt?: string;
  unreadCount?: number;
  sendWindow?: SendWindow | null;
  connection?: Inbox | null;
  lastMessage?: Message | null;
  contactTags?: string[];
  contactCategoriaId?: string | null;
  contactCategoria?: {
    id: string;
    nome: string;
    cor?: string | null;
    funnelId?: string | null;
    funnel?: { id: string; nome: string; cor?: string | null } | null;
  } | null;
};

type ContactDetails = {
  id: string;
  nome: string;
  telefone: string;
  email?: string | null;
  endereco?: string | null;
  cnpj?: string | null;
  tags?: string[];
  observacoes?: string | null;
  resumoIA?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  youtube?: string | null;
  tiktok?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  threads?: string | null;
  bluesky?: string | null;
  reddit?: string | null;
  telegram?: string | null;
  googleBusiness?: string | null;
  followUpAtivo?: boolean;
  iaAtiva?: boolean;
  categoriaId?: string | null;
  ultimaMensagem?: string | null;
  ultimoFollowUpEnviadoEm?: string | null;
  categoria?: { nome?: string; cor?: string } | null;
  criadoEm?: string;
  atualizadoEm?: string;
};

type ContactEditForm = {
  nome: string;
  email: string;
  endereco: string;
  cnpj: string;
  categoriaId: string;
  tags: string[];
  followUpAtivo: boolean;
  observacoes: string;
  resumoIA: string;
  instagram: string;
  facebook: string;
  youtube: string;
  tiktok: string;
  twitter: string;
  linkedin: string;
  threads: string;
  bluesky: string;
  reddit: string;
  telegram: string;
  googleBusiness: string;
};

type CategoryOption = { id: string; nome: string; funnelId?: string | null; funnelOrder?: number | null; funnel?: { id: string; nome: string } | null };
type ContactSearchItem = { id: string; nome: string; telefone: string; email?: string | null };
type CrmUserOption = { id: string; nome?: string; email: string; ativo?: boolean };
type SocialContactKey = 'instagram' | 'facebook' | 'youtube' | 'tiktok' | 'twitter' | 'linkedin' | 'threads' | 'bluesky' | 'reddit' | 'telegram' | 'googleBusiness';
const CONVERSATION_PAGE_SIZE = 60;

type Message = {
  id: string;
  direction: 'incoming' | 'outgoing';
  senderName?: string;
  body?: string;
  messageType: string;
  attachments?: Array<{ url?: string; type?: string; contentType?: string; filename?: string }>;
  sentAt: string;
  externalMessageId?: string;
  replyToExternalId?: string | null;
  status?: string;
  rawPayload?: Record<string, unknown>;
};

// Janela de 24h das plataformas oficiais (WhatsApp/Instagram/Facebook), calculada
// no backend a partir da ultima mensagem recebida.
type SendWindow = {
  applies?: boolean;
  platform?: string;
  open?: boolean;
  neverReplied?: boolean;
  lastInboundAt?: string | null;
  expiresAt?: string | null;
  minutesLeft?: number;
  enforced?: boolean;
  // Instagram/Messenger: fora das 24h ainda dá para responder com a tag de
  // atendimento humano (7 dias). O WhatsApp nao tem equivalente.
  humanAgentFallback?: boolean;
};

type GroupParticipant = { jid?: string | null; phone?: string | null; name?: string | null; isAdmin?: boolean | null };

type ChatAttachment = {
  url?: string;
  type?: string;
  contentType?: string;
  filename?: string;
  // contato compartilhado (vCard)
  name?: string | null;
  phones?: string[];
  org?: string | null;
  vcard?: string;
  // localizacao
  latitude?: number;
  longitude?: number;
  address?: string | null;
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SOCIAL_CONTACT_FIELDS: Array<{ key: SocialContactKey; label: string }> = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'twitter', label: 'X/Twitter' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'threads', label: 'Threads' },
  { key: 'bluesky', label: 'Bluesky' },
  { key: 'reddit', label: 'Reddit' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'googleBusiness', label: 'Google Business' }
];

function socialFieldForPlatform(platform?: string): SocialContactKey | null {
  const map: Record<string, SocialContactKey> = {
    instagram: 'instagram',
    facebook: 'facebook',
    youtube: 'youtube',
    tiktok: 'tiktok',
    twitter: 'twitter',
    x: 'twitter',
    linkedin: 'linkedin',
    threads: 'threads',
    bluesky: 'bluesky',
    reddit: 'reddit',
    telegram: 'telegram',
    googlebusiness: 'googleBusiness',
    google_business: 'googleBusiness'
  };
  return map[String(platform || '').toLowerCase()] || null;
}

function socialHandleFromConversation(conversation?: Conversation | null) {
  if (!conversation || conversation.platform === 'whatsapp') return '';
  const metadata = conversation.metadata || {};
  return String(conversation.contactHandle || conversation.externalContactId || metadata.authorUsername || metadata.senderUsername || metadata.senderId || conversation.externalThreadId || '').trim();
}

function socialProfileUrl(conversation?: Conversation | null) {
  if (!conversation || conversation.platform === 'whatsapp') return '';
  const metadata = conversation.metadata || {};
  const direct = String(metadata.profileUrl || metadata.authorUrl || metadata.senderUrl || metadata.userUrl || '').trim();
  if (direct) return direct;
  const handle = socialHandleFromConversation(conversation).replace(/^@/, '').trim();
  if (!handle) return '';
  const platform = String(conversation.platform || '').toLowerCase();
  const map: Record<string, string> = {
    instagram: `https://www.instagram.com/${handle}`,
    facebook: `https://www.facebook.com/${handle}`,
    youtube: handle.startsWith('UC') ? `https://www.youtube.com/channel/${handle}` : `https://www.youtube.com/@${handle}`,
    tiktok: `https://www.tiktok.com/@${handle}`,
    twitter: `https://x.com/${handle}`,
    x: `https://x.com/${handle}`,
    linkedin: `https://www.linkedin.com/in/${handle}`,
    threads: `https://www.threads.net/@${handle}`,
    bluesky: `https://bsky.app/profile/${handle}`,
    reddit: `https://www.reddit.com/user/${handle}`,
    telegram: `https://t.me/${handle}`
  };
  return map[platform] || '';
}

function isConversationAiEnabled(conversation?: Conversation | null) {
  if (!conversation) return false;
  return conversation.metadata?.aiEnabled === true || conversation.metadata?.aiEnabled === 'true';
}

function isConversationPinned(conversation?: Conversation | null) {
  if (!conversation) return false;
  return conversation.metadata?.pinned === true || conversation.metadata?.pinned === 'true';
}

function assignedUserId(conversation?: Conversation | null) {
  return String(conversation?.metadata?.assignedUserId || '').trim();
}

function assignedUserName(conversation?: Conversation | null) {
  return String(conversation?.metadata?.assignedUserName || '').trim();
}

// Tag de atribuicao na lista: mostra apenas o primeiro nome (ou o usuario do email).
function shortUserLabel(name: string) {
  const value = String(name || '').trim();
  if (!value) return '';
  const base = value.includes('@') ? value.split('@')[0] : value;
  return base.split(/\s+/)[0];
}

function sortConversationsPinnedFirst(items: Conversation[]) {
  const pinTime = (item: Conversation) => Number(new Date(String(item.metadata?.pinnedAt || 0)).getTime() || 0);
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const pa = isConversationPinned(a.item) ? 1 : 0;
      const pb = isConversationPinned(b.item) ? 1 : 0;
      if (pa !== pb) return pb - pa;
      if (pa && pb) return pinTime(b.item) - pinTime(a.item);
      return a.index - b.index; // mantem a ordem original (por ultima mensagem)
    })
    .map((entry) => entry.item);
}

function isInboxAiEnabled(inbox?: Inbox | Conversation | null) {
  return inbox?.metadata?.aiEnabled === true || inbox?.metadata?.aiEnabled === 'true';
}

function inboxAiChannels(inbox?: Inbox | Conversation | null) {
  if (!inbox) return [] as Array<{ key: string; label: string; metadataKey: string }>;
  if (inbox.provider === 'marketplace-hub' && inbox.platform === 'mercadolivre') {
    return [
      { key: 'question', label: 'Perguntas', metadataKey: 'aiQuestionEnabled' },
      { key: 'sale', label: 'Mensagens', metadataKey: 'aiSaleEnabled' }
    ];
  }
  if (inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee') {
    return [
      { key: 'dm', label: 'Chat', metadataKey: 'aiDmEnabled' },
      { key: 'comment', label: 'Comentarios', metadataKey: 'aiCommentEnabled' }
    ];
  }
  return [
    { key: 'dm', label: 'DM', metadataKey: 'aiDmEnabled' },
    { key: 'comment', label: 'Comentarios', metadataKey: 'aiCommentEnabled' }
  ];
}

function isInboxChannelAiEnabled(inbox?: Inbox | Conversation | null, channelType?: string) {
  const channel = inboxAiChannels(inbox).find((item) => item.key === channelType || (channelType === 'dm' && item.key === 'dm'));
  return Boolean(channel && (inbox?.metadata?.[channel.metadataKey] === true || inbox?.metadata?.[channel.metadataKey] === 'true'));
}

function isEffectiveConversationAiEnabled(conversation?: Conversation | null, inbox?: Inbox | Conversation | null) {
  return isInboxAiEnabled(inbox) && isInboxChannelAiEnabled(inbox, conversation?.channelType) && isConversationAiEnabled(conversation);
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/crm-chat${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {})
    } as HeadersInit
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `HTTP ${response.status}`);
  return data.data;
}

async function contactRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/contatos${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {})
    } as HeadersInit
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `HTTP ${response.status}`);
  return data;
}

async function rawApiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {})
    } as HeadersInit
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `HTTP ${response.status}`);
  return data;
}

function formatTime(value?: string) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Motivo da nao-entrega, quando existe. Vem de duas fontes: da recusa na hora do
// envio (janela de 24h fechada, midia acima do limite) e do webhook message.failed
// da Zernio, que e por onde o Meta avisa que nao entregou depois de ja ter aceito.
function messageFailure(message: Message): string | null {
  if (message.status !== 'failed') return null;
  const failure = (message.rawPayload as any)?.failure;
  return String(failure?.message || 'A plataforma recusou a entrega desta mensagem.');
}

function officialPlatformName(platform?: string): string {
  const key = String(platform || '').toLowerCase();
  if (key === 'whatsapp') return 'o WhatsApp';
  if (key === 'instagram') return 'o Instagram';
  if (key === 'facebook' || key === 'messenger') return 'o Facebook';
  return 'a plataforma';
}

function deliveryLabel(message: Message): string {
  if (message.status === 'sending') return 'enviando...';
  if (message.status === 'failed') return 'não entregue';
  if (message.status === 'read') return 'lida';
  if (message.status === 'delivered') return 'entregue';
  return 'enviada';
}

function formatMessageBody(text: string): React.ReactNode {
  // Suporta **negrito** e *italico* (WhatsApp-style). Processa negrito primeiro.
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2] !== undefined) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={key++}>{match[3]}</em>);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

function providerLabel(provider: string) {
  if (provider === 'waha') return 'WhatsApp Nao Oficial';
  if (provider === 'official') return 'WhatsApp Oficial';
  return provider;
}

function platformLabel(platform: string) {
  const labels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    twitter: 'X/Twitter',
    tiktok: 'TikTok',
    telegram: 'Telegram',
    bluesky: 'Bluesky',
    reddit: 'Reddit',
    mercadolivre: 'Mercado Livre',
    shopee: 'Shopee'
  };
  return labels[platform] || platform;
}

function channelLabel(channelType: string) {
  if (channelType === 'dm') return 'DM';
  if (channelType === 'comment') return 'Comentário';
  if (channelType === 'group') return 'Grupo';
  if (channelType === 'question') return 'Pergunta';
  if (channelType === 'sale') return 'Mensagem venda';
  if (channelType === 'claim') return 'Reclamação';
  return channelType;
}

function hasChatIntegration(item?: Inbox | Conversation | null) {
  const platform = String(item?.platform || '').toLowerCase();
  if (item?.provider === 'marketplace-hub' && ['mercadolivre', 'shopee'].includes(platform)) return true;
  return ['whatsapp', 'instagram', 'facebook', 'youtube', 'twitter', 'x', 'reddit', 'bluesky', 'telegram', 'linkedin', 'threads'].includes(platform);
}

function connectionName(item?: Inbox | Conversation | null) {
  if (!item) return 'Todas';
  return item.title || item.contactName || item.contactHandle || item.externalAccountId || `${platformLabel(item.platform)} ${providerLabel(item.provider)}`;
}

function formatPhone(phone?: string) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 14) return '';
  if (digits.length === 13 && digits.startsWith('55')) return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  if (digits.length === 12 && digits.startsWith('55')) return `+55 (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  return `+${digits}`;
}

function phoneFromWahaMetadata(metadata?: Record<string, unknown>, externalThreadId?: string) {
  const values = [metadata?.phoneJid, metadata?.contactJid, metadata?.chatId, metadata?.jid, metadata?.remoteJid, metadata?.from, metadata?.to, externalThreadId]
    .map((value) => String(value || ''));
  const jid = values.find((value) => /@(c\.us|s\.whatsapp\.net)$/i.test(value));
  return formatPhone(jid || '');
}

function isWhatsAppJid(value?: string) {
  return /@(c\.us|s\.whatsapp\.net|g\.us|lid|newsletter)$/i.test(String(value || '').trim());
}

function labelForConversation(conversation: Conversation) {
  if (conversation.channelType === 'group') {
    const groupCandidates = [conversation.groupName, conversation.title, textMeta(conversation.metadata, 'groupName'), textMeta(conversation.metadata, 'chatName')]
      .filter((value): value is string => Boolean(value && !isWhatsAppJid(value)));
    return groupCandidates[0] || 'Grupo';
  }
  if (conversation.provider === 'waha' && conversation.channelType !== 'group') {
    const phone = phoneFromWahaMetadata(conversation.metadata, conversation.externalThreadId) || formatPhone(conversation.contactPhone);
    if (conversation.contactName && !isWhatsAppJid(conversation.contactName)) return conversation.contactName;
    if (phone) return phone;
  }
  const candidates = [conversation.contactName, conversation.groupName, conversation.contactHandle, conversation.title]
    .filter((value): value is string => Boolean(value && !isWhatsAppJid(value)));
  return candidates[0] || formatPhone(conversation.contactPhone) || 'Sem nome';
}

function normalizePhoneLabel(value?: string | null) {
  return formatPhone(value || '') || String(value || '').trim();
}

function jidFromUnknown(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'object') {
    const item = value as Record<string, unknown>;
    return jidFromUnknown(item._serialized || item.id || item.remoteJid || item.remote || item.user || item.jid);
  }
  const text = String(value).trim();
  const direct = text.match(/([0-9]{6,}|[0-9-]+)@(c\.us|s\.whatsapp\.net|g\.us|lid|newsletter)/i)?.[0];
  return direct || '';
}

function phoneFromJid(value?: string | null) {
  if (!value) return '';
  const local = String(value).split('@')[0];
  return normalizePhoneLabel(local);
}

function groupSenderLabel(message: Message) {
  const raw = (message.rawPayload || {}) as Record<string, unknown>;
  const payload = ((raw.payload || raw.data || raw.message || raw) || {}) as Record<string, unknown>;
  const nestedData = (payload._data || {}) as Record<string, unknown>;
  const nestedId = ((nestedData.id || (payload.id as Record<string, unknown>) || {}) || {}) as Record<string, unknown>;
  const candidates = [
    message.senderName,
    phoneFromJid(jidFromUnknown(payload.participant)),
    phoneFromJid(jidFromUnknown(payload.author)),
    phoneFromJid(jidFromUnknown(nestedData.author)),
    phoneFromJid(jidFromUnknown(nestedId.participant)),
  ].map((value) => String(value || '').trim()).filter(Boolean);
  return candidates[0] || 'Participante';
}

function groupParticipantsFromConversation(conversation?: Conversation | null): GroupParticipant[] {
  const value = conversation?.metadata?.participants;
  return Array.isArray(value) ? value.filter((item): item is GroupParticipant => typeof item === 'object' && item !== null) : [];
}

function avatarUrlForConversation(conversation: Conversation) {
  const metadata = conversation.metadata || {};
  const candidates = [
    metadata.profilePictureUrl,
    metadata.profilePicUrl,
    metadata.profilePic,
    metadata.avatarUrl,
    metadata.avatar,
    metadata.thumbnail,
    metadata.senderAvatar,
    metadata.authorAvatar,
    metadata.contactAvatar,
    metadata.picture,
    metadata.photoUrl,
  ];
  return candidates.find((value): value is string => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/');
  }) || '';
}

function ConnectionIcon({ platform, provider, size = 'md' }: { platform?: string; provider?: string; size?: 'xs' | 'badge' | 'sm' | 'md' }) {
  const iconPlatform = provider === 'waha' ? 'whatsapp' : (platform || 'whatsapp');
  const colors = PLATFORM_COLORS[iconPlatform] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
  const iconSize = size === 'xs' ? 9 : size === 'badge' ? 11 : size === 'sm' ? 15 : 18;
  const badge = size !== 'xs' && iconPlatform === 'whatsapp' ? (provider === 'waha' ? 'Nao Oficial' : provider === 'official' ? 'Oficial' : '') : '';
  const badgeFontSize = size === 'badge' ? 'text-[6px]' : 'text-[8px]';
  return (
    <span className={`${size === 'xs' ? 'h-4 w-4' : size === 'badge' ? 'h-5 w-5' : size === 'sm' ? 'h-7 w-7' : 'h-9 w-9'} relative inline-flex items-center justify-center rounded-md border ${colors.bg} ${colors.text} ${colors.border}`}>
      <PlatformIcon platform={iconPlatform} size={iconSize} />
      {badge && (
        <span className={`absolute -bottom-2 left-1/2 max-w-[54px] -translate-x-1/2 truncate rounded bg-white px-0.5 py-px ${badgeFontSize} font-semibold leading-none text-slate-700 shadow-sm ring-1 ring-slate-200`}>
          {badge}
        </span>
      )}
    </span>
  );
}

function ConversationAvatar({ conversation, connection }: { conversation: Conversation; connection?: Inbox | null }) {
  const avatarUrl = avatarUrlForConversation(conversation);
  const label = labelForConversation(conversation);
  const initial = label.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="relative inline-flex h-11 w-11 flex-shrink-0 items-center justify-center">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-11 w-11 rounded-full border border-slate-200 object-cover bg-slate-100" loading="lazy" />
      ) : (
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-sm font-semibold text-slate-600">
          {initial}
        </span>
      )}
      <span className="absolute -bottom-1 -right-1">
        <ConnectionIcon platform={connection?.platform || conversation.platform} provider={connection?.provider || conversation.provider} size="badge" />
      </span>
    </span>
  );
}

function textMeta(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value : '';
}

function boolMeta(metadata: Record<string, unknown> | undefined, key: string) {
  return metadata?.[key] === true || metadata?.[key] === 'true';
}

// Extensão do arquivo a partir do que o MediaRecorder REALMENTE gravou.
//
// Cada navegador grava num container diferente — Chrome/Firefox em WebM/Opus,
// Safari (iPhone e Mac) em MP4/AAC — e o nome do arquivo é o que decide o
// Content-Type com que ele volta servido (express.static deduz pela extensão).
// Gravar MP4 com nome ".webm" fazia o arquivo voltar como video/webm: o Safari
// não toca WebM (nem o preview do áudio recém-gravado) e o WhatsApp recusa a
// mídia no envio.
function audioExtensionForMime(mimeType?: string | null) {
  const base = String(mimeType || '').split(';')[0].trim().toLowerCase();
  if (base === 'audio/mp4' || base === 'audio/x-m4a' || base === 'audio/aac' || base === 'audio/mpeg') {
    return base === 'audio/mpeg' ? 'mp3' : 'm4a';
  }
  if (base === 'audio/ogg' || base === 'application/ogg') return 'ogg';
  if (base === 'audio/wav' || base === 'audio/x-wav') return 'wav';
  if (base === 'audio/webm') return 'webm';
  return 'webm';
}

function isAudioAttachment(item?: ChatAttachment | null) {
  const source = `${item?.contentType || ''} ${item?.type || ''} ${item?.filename || ''} ${item?.url || ''}`.toLowerCase();
  return source.includes('audio') || source.includes('voice') || source.includes('ptt') || /\.(mp3|wav|ogg|oga|m4a|aac|opus|webm)(\?|$)/i.test(item?.url || '');
}

function MicrophoneIcon({ active = false }: { active?: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4M8 22h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {active && <circle cx="18" cy="5" r="2" fill="currentColor" />}
    </svg>
  );
}

function RecordingWaveform({ samples }: { samples: number[] }) {
  const bars = samples.length ? samples : [0.2, 0.45, 0.3, 0.65, 0.4, 0.55, 0.25];
  return (
    <span className="flex h-10 flex-1 items-center gap-1" aria-hidden="true">
      {bars.map((value, index) => (
        <span key={index} className="w-1 rounded-full bg-current transition-all duration-75" style={{ height: Math.max(4, Math.round(value * 36)) }} />
      ))}
    </span>
  );
}

type AiAssistModal = { open: boolean; tab: 'reply' | 'rewrite'; rewriteMode: 'friendly' | 'fix_spelling' | 'force_sale' | 'improve_context'; loading: boolean; result: string };

type QuickReplyStep = { id?: string; order: number; type: string; content: string; caption: string; delayMs: number };
type QuickReplyItem = { id: string; name: string; description?: string; steps: QuickReplyStep[] };
type ScheduledMessage = { id: string; kind: string; body?: string | null; attachments?: ChatAttachment[]; quickReplyId?: string | null; scheduledFor: string; status: string; error?: string | null; quickReply?: QuickReplyItem | null; flowEventId?: string | null; template?: { name?: string } | null };

type MediaLibraryItem = { id: string; url: string; name?: string; originalName?: string; mimeType?: string; type?: string };

// Template aprovado da API oficial (WhatsApp Business), disparado de dentro da
// conversa com o atalho "#". `positions` já vem do backend na ordem exata em que
// a Meta espera os valores (cabeçalho de texto → corpo → botões de link).
type TemplatePosition = { key: string; section: string; slot: string; label: string };
type OfficialTemplate = {
  name: string;
  language: string;
  category?: string | null;
  status: string;
  components?: TemplateComponent[];
  variableOrder?: string[];
  positions: TemplatePosition[];
  headerFormat?: string | null;
};
type TemplateComponent = {
  type?: string;
  format?: string;
  text?: string;
  buttons?: Array<{ type?: string; text?: string; url?: string }>;
};

// Prefixo de posição mapeada para TEXTO FIXO — precisa bater com LITERAL_PREFIX
// de backend/services/templateVariableMapping.
const TEMPLATE_TEXT_PREFIX = 'text:';

const REWRITE_MODES: Array<{ value: 'friendly' | 'fix_spelling' | 'force_sale' | 'improve_context'; label: string; desc: string; icon: string }> = [
  { value: 'friendly', label: 'Tom Amigável', desc: 'Reescreve com tom caloroso e próximo', icon: '😊' },
  { value: 'fix_spelling', label: 'Corrigir Ortografia', desc: 'Corrige erros de gramática e pontuação', icon: '✏️' },
  { value: 'force_sale', label: 'Forçar Venda', desc: 'Usa persuasão e psicologia de vendas', icon: '🎯' },
  { value: 'improve_context', label: 'Melhorar com Contexto', desc: 'Melhora usando dados da empresa (Base de conhecimento)', icon: '🧠' },
];

const EMOJI_CATEGORIES = [
  { label: '😊', name: 'Rostos', emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥸','😎','🤓','🧐','😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖'] },
  { label: '👋', name: 'Gestos', emojis: ['👋','🤚','🖐','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','🤲','🤝','🙏','✍️','💅','🤳','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🫀','🫁','🧠','🦷','🦴','👀','👁','👅','👄','💋','🩸'] },
  { label: '❤️', name: 'Símbolos', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✝️','☪️','🕉','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️','📴','📳','🈶','🈚','🈸','🈺','🈷️','✴️','🆚','💮','🉐','㊙️','㊗️','🈴','🈵','🈹','🈲','🅰️','🅱️','🆎','🆑','🅾️','🆘','❌','⭕','🛑','⛔','📛','🚫','💯','💢','♨️','🚷','🚯','🚳','🚱','🔞','📵','🔕','🔇'] },
  { label: '🎉', name: 'Atividades', emojis: ['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🏓','🏸','🏒','🥍','🏑','🏏','🎿','🛷','🥌','🪃','🪁','🥅','⛳','🎣','🤿','🎽','🎿','🛹','🛼','🪂','🤸','🤼','🤺','🤾','🏌','🏇','🧘','🏋','🤼','⛷','🏊','🚣','🧗','🚴','🤾','🤺','🤼','🎮','🕹','🎲','♟','🃏','🀄','🎴','🎭','🎨','🧵','🧶','🎪','🎤','🎧','🎼','🎹','🥁','🪘','🎷','🎺','🎸','🪕','🎻','🎬','🎤','🎉','🎊','🎀','🎁','🎗','🎟','🎫'] },
  { label: '🍕', name: 'Comida', emojis: ['🍏','🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🍆','🥑','🥦','🥬','🥒','🌶','🫑','🧄','🧅','🥔','🍠','🥐','🥯','🍞','🥖','🥨','🧀','🥚','🍳','🧈','🥞','🧇','🥓','🥩','🍗','🍖','🦴','🌭','🍔','🍟','🍕','🫓','🥪','🥙','🧆','🌮','🌯','🫔','🥗','🥘','🫕','🥫','🍝','🍜','🍲','🍛','🍣','🍱','🥟','🦪','🍤','🍙','🍘','🍥','🥮','🍢','🧁','🍰','🎂','🍮','🍭','🍬','🍫','🍿','🍩','🍪','🌰','🥜','🍯','🧃','🥤','🧋','☕','🍵','🍺','🍻','🍸','🍹','🧉','🍾','🍷'] },
  { label: '✈️', name: 'Viagem', emojis: ['🚗','🚕','🚙','🚌','🚎','🏎','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','🏍','🛵','🛺','🚲','🛴','🛹','🛼','🚏','🛣','🛤','⛽','🛞','🚨','🚥','🚦','🛑','🚧','⚓','🛟','⛵','🛶','🚤','🛳','⛴','🛥','🚢','✈️','🛩','🛫','🛬','🪂','💺','🚁','🚟','🚠','🚡','🛰','🚀','🛸','🪐','🌍','🌎','🌏','🌐','🗺','🧭','🏔','⛰','🌋','🗻','🏕','🏖','🏜','🏝','🏞','🏟','🏛','🏗','🏘','🏚','🏠','🏡','🏢','🏣','🏤','🏥','🏦','🏨','🏩','🏪','🏫','🏬','🏭','🏯','🏰','💒','🗼','🗽','⛪','🕌','🛕','🕍','⛩','🕋','⛲','⛺','🌁','🌃','🏙','🌄','🌅','🌆','🌇','🌉','🌌','🌠','🎇','🎆','🌇','🌈'] },
];

function EmojiPicker({ category, onCategoryChange, onPick }: { category: number; onCategoryChange: (i: number) => void; onPick: (e: string) => void }) {
  return (
    <div className="absolute bottom-full right-0 mb-2 w-72 rounded-xl border border-slate-200 bg-white shadow-2xl z-30" onClick={(e) => e.stopPropagation()}>
      {/* Category tabs */}
      <div className="flex border-b border-slate-100">
        {EMOJI_CATEGORIES.map((cat, i) => (
          <button
            key={i}
            onClick={() => onCategoryChange(i)}
            title={cat.name}
            className={`flex-1 py-2 text-base transition-colors ${category === i ? 'border-b-2 border-blue-500 bg-blue-50' : 'hover:bg-slate-50'}`}
          >
            {cat.label}
          </button>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-8 gap-0 p-2 max-h-52 overflow-y-auto">
        {EMOJI_CATEGORIES[category].emojis.map((emoji) => (
          <button
            key={emoji}
            onClick={() => onPick(emoji)}
            className="flex h-8 w-8 items-center justify-center rounded text-lg hover:bg-slate-100 transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

export function CrmChatPage() {
  const { user } = useAuth();
  const { currentTenant } = useTenants();
  const { clearConversation } = useChatNotifications();
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [selectedInboxKey, setSelectedInboxKey] = useState('all');
  const [assignmentScope, setAssignmentScope] = useState<'mine' | 'all'>('all');
  const [channelTypeFilter, setChannelTypeFilter] = useState<string>('all');
  // Ordenacao da lista. 'unread' e resolvida no servidor (sort=unread), para que
  // as nao lidas venham primeiro mesmo estando fora da primeira pagina.
  const [sortMode, setSortMode] = useState<'recent' | 'unread'>(
    () => (localStorage.getItem('crm_chat_sort_mode') === 'unread' ? 'unread' : 'recent')
  );
  // allConversations guarda TODAS as conversas carregadas; a lista visivel e
  // derivada via useMemo para que trocar de inbox seja instantaneo (sem re-fetch).
  const [allConversations, setAllConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Conversa que estava nao lida quando foi aberta (ver ordenacao por nao lidas).
  const [stickyUnreadId, setStickyUnreadId] = useState<string | null>(null);
  // Conversas abertas diretamente por ID podem estar fora da pagina das 60
  // mais recentes. Mantemos esses IDs durante refresh/polling para o cabecalho
  // e os dados do contato nao desaparecerem logo depois de abrir.
  const directlyLoadedConversationIdsRef = useRef<Set<string>>(new Set());
  // Quantas paginas do "carregar mais" ja foram trazidas no escopo atual. Um
  // refetch da pagina 1 nao pode reescrever `hasMoreConversations` quando o
  // usuario ja paginou — senao o botao desaparece com conversas ainda por vir.
  const pagesLoadedRef = useRef(1);
  // Cursor real da paginacao (o que o servidor devolveu no fim da ultima pagina)
  // e a query a que ele pertence: inbox+busca+ordenacao. Trocar a query invalida
  // o cursor, senao a proxima pagina sairia de um ponto de outro conjunto.
  const paginationCursorRef = useRef<string | null>(null);
  const lastQueryKeyRef = useRef<string>('');
  // Sequencia das listagens: so a resposta mais recente pode mexer na paginacao.
  const listFetchSeqRef = useRef(0);
  // Ids apagados nesta aba: um GET de listagem que ja estava em voo quando o
  // DELETE aconteceu traria a conversa de volta.
  const deletedConversationIdsRef = useRef<Set<string>>(new Set());
  // Etiquetas do tenant (nome -> cor) para renderizar os chips nos cards de conversa
  const [contactLabels, setContactLabels] = useState<import('../types').ContactLabel[]>([]);
  useEffect(() => { apiService.getContactLabels().then(setContactLabels).catch(() => setContactLabels([])); }, []);
  const labelColorMap = useMemo(() => new Map(contactLabels.map((label) => [label.name, label.color])), [contactLabels]);
  const location = useLocation();
  // Deep-link: quando chega do Kanban ("Enviar mensagem"), abre a conversa indicada.
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(
    (location.state as { selectConversationId?: string } | null)?.selectConversationId || null
  );

  // Ao chegar via deep-link, mostra todas as caixas e limpa o state de navegacao
  // (para um refresh nao reabrir a conversa indefinidamente).
  useEffect(() => {
    if (!pendingSelectId) return;
    setSelectedInboxKey('all');
    try { window.history.replaceState({}, document.title); } catch { /* noop */ }
  }, [pendingSelectId]);

  // Seleciona a conversa alvo mesmo quando ela e antiga e nao esta na primeira
  // pagina. Antes, o deep-link ficava pendurado para sempre nesse caso.
  useEffect(() => {
    if (!pendingSelectId) return;
    let active = true;
    const loaded = allConversations.some((conversation) => conversation.id === pendingSelectId);
    if (loaded) {
      directlyLoadedConversationIdsRef.current.add(pendingSelectId);
      setSelectedId(pendingSelectId);
      setPendingSelectId(null);
      return;
    }
    apiRequest<{ conversation?: Conversation; messages?: Message[] }>(`/conversations/${pendingSelectId}/messages`)
      .then((data) => {
        if (!active || !data.conversation) return;
        directlyLoadedConversationIdsRef.current.add(data.conversation.id);
        setAllConversations((items) => {
          const merged = new Map(items.map((item) => [item.id, item]));
          merged.set(data.conversation!.id, data.conversation!);
          return sortConversationsPinnedFirst(Array.from(merged.values()));
        });
        setSelectedId(data.conversation.id);
        setPendingSelectId(null);
      })
      .catch((error) => {
        if (!active) return;
        setPendingSelectId(null);
        toast.error(error instanceof Error ? error.message : 'Nao foi possivel abrir a conversa solicitada.');
      });
    return () => { active = false; };
  }, [pendingSelectId, allConversations]);

  // "Nova conversa" — so faz sentido em caixas do WhatsApp Nao Oficial (WAHA),
  // onde sabemos a sessao (externalAccountId) para iniciar o envio.
  const waInbox = useMemo(
    () => inboxes.find((inbox) => `${inbox.provider}:${inbox.externalAccountId}` === selectedInboxKey) || null,
    [inboxes, selectedInboxKey]
  );
  const isWahaInbox = waInbox?.provider === 'waha' && !!waInbox?.externalAccountId;
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newConvQuery, setNewConvQuery] = useState('');
  const [newConvResults, setNewConvResults] = useState<Array<{ id: string; nome: string; telefone: string }>>([]);
  const [newConvSearching, setNewConvSearching] = useState(false);
  const [newConvStarting, setNewConvStarting] = useState(false);

  // Busca contatos por nome/telefone enquanto digita (debounce).
  useEffect(() => {
    if (!newConvOpen) return;
    const query = newConvQuery.trim();
    if (query.length < 2) { setNewConvResults([]); setNewConvSearching(false); return; }
    let active = true;
    setNewConvSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.getContacts({ search: query, pageSize: 8 });
        if (active) setNewConvResults((res.contacts || []).map((c) => ({ id: c.id, nome: c.nome, telefone: c.telefone })));
      } catch {
        if (active) setNewConvResults([]);
      } finally {
        if (active) setNewConvSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [newConvOpen, newConvQuery]);

  const startNewConversation = async (params: { contactId?: string; phone?: string }) => {
    if (!waInbox?.externalAccountId || newConvStarting) return;
    setNewConvStarting(true);
    const loadingToastId = toast.loading('Verificando número e preparando conversa...');
    try {
      const res = await apiService.startCrmConversation({ ...params, externalAccountId: waInbox.externalAccountId });
      const data = res.data;
      if ((data.status === 'existing' || data.status === 'created') && data.conversationId) {
        setNewConvOpen(false);
        setNewConvQuery('');
        setNewConvResults([]);
        setSelectedInboxKey('all');
        setPendingSelectId(data.conversationId);
        loadConversations().catch(() => { /* noop */ });
      } else if (data.status === 'no_phone') {
        toast.error('Telefone inválido.');
      } else if (data.status === 'not_on_whatsapp') {
        toast.error('Este número não está disponível no WhatsApp.');
      } else if (data.status === 'no_inbox') {
        toast.error('Nenhuma conexão WhatsApp disponível.');
      } else {
        toast.error('Não foi possível iniciar a conversa.');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao iniciar conversa');
    } finally {
      toast.dismiss(loadingToastId);
      setNewConvStarting(false);
    }
  };

  const [messages, setMessages] = useState<Message[]>([]);
  // Mensagens ainda em voo (ver sendPayload): ficam fora de `messages` para
  // sobreviverem às recargas que acontecem durante o envio.
  const [pendingMessages, setPendingMessages] = useState<Array<{ id: string; conversationId: string; message: Message }>>([]);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  const [search, setSearch] = useState('');
  // Texto digitado filtra localmente na hora; o que vai ao servidor e a busca
  // aplicada (Enter, ou o campo ser esvaziado).
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<ChatAttachment | null>(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingSamples, setRecordingSamples] = useState<number[]>(Array(28).fill(0.15));
  const [aiAssist, setAiAssist] = useState<AiAssistModal>({ open: false, tab: 'reply', rewriteMode: 'friendly', loading: false, result: '' });
  const [quickReplySuggestions, setQuickReplySuggestions] = useState<QuickReplyItem[]>([]);
  const [quickReplyPreview, setQuickReplyPreview] = useState<QuickReplyItem | null>(null);
  const [quickReplyFiring, setQuickReplyFiring] = useState(false);
  const [officialTemplates, setOfficialTemplates] = useState<OfficialTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateModal, setTemplateModal] = useState<{ conversationId: string; template: OfficialTemplate; params: string[]; headerMedia: string } | null>(null);
  const [templateSending, setTemplateSending] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleListOpen, setScheduleListOpen] = useState(false);
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  // Agendar reunião (Chat CRM -> agenda do Flow)
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [conversationMeetings, setConversationMeetings] = useState<ExistingMeeting[]>([]);
  const [chatCapabilities, setChatCapabilities] = useState({ shopeeChatSendEnabled: false });
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduleQuickReplyId, setScheduleQuickReplyId] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [savingInboxAiId, setSavingInboxAiId] = useState<string | null>(null);
  const { request: requestCreditConfirm, modal: creditConfirmModal } = useCreditConfirm();
  const [clearingAiHistory, setClearingAiHistory] = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  // Clone de Voz (Fish Audio): catálogo do tenant + os dois modais.
  const [voiceClones, setVoiceClones] = useState<VoiceClone[]>([]);
  const [voiceManagerOpen, setVoiceManagerOpen] = useState(false);
  const [voiceMessageOpen, setVoiceMessageOpen] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState<MediaLibraryItem[]>([]);
  const [mediaLibraryLoading, setMediaLibraryLoading] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>('all');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const [convMenuId, setConvMenuId] = useState<string | null>(null);
  const [users, setUsers] = useState<CrmUserOption[]>([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningConversation, setAssigningConversation] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(() => new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const draftTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [contactPanelOpen, setContactPanelOpen] = useState(false);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactEditing, setContactEditing] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactForm, setContactForm] = useState<ContactEditForm | null>(null);
  const contactLoadRequestRef = useRef(0);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [chatFunnels, setChatFunnels] = useState<Funnel[]>([]);
  const [pipelineSavingContactId, setPipelineSavingContactId] = useState<string | null>(null);
  const [funnelMoveModal, setFunnelMoveModal] = useState<{ conversation: Conversation; funnelId: string; categoriaId: string } | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<ContactSearchItem[]>([]);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserFrameRef = useRef<number | null>(null);
  const sendAfterRecordingRef = useRef(false);
  const inboxesRef = useRef<Inbox[]>([]);
  const notifyAudioCtxRef = useRef<AudioContext | null>(null);
  const lastBeepAtRef = useRef(0);
  const reloadTimerRef = useRef<number | null>(null);
  const msgReloadTimerRef = useRef<number | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  // Scroll da lista lateral. No mobile o <aside> recebe display:none ao abrir uma
  // conversa e o browser zera o scrollTop; guardamos a posicao para devolver o
  // operador exatamente onde ele estava na lista.
  const listScrollRef = useRef<HTMLDivElement>(null);
  const listScrollTopRef = useRef(0);
  // Painel de mensagens: true enquanto o operador esta no fim da conversa.
  const followBottomRef = useRef(true);
  // Stream de eventos ativo? Enquanto estiver, o polling de fallback fica quieto.
  const streamConnectedRef = useRef(false);
  // Upload em voo (ref, não estado: o guard precisa valer no mesmo tick, antes
  // de qualquer re-render).
  const uploadingRef = useRef(false);
  // Conversa aberta no momento em que uma resposta de envio chega (o operador pode
  // ter trocado de conversa enquanto a mídia era convertida e enviada).
  const selectedIdRef = useRef<string | null>(null);

  // Mantem a lista de caixas acessivel dentro do handler do SSE sem recriar a conexao.
  useEffect(() => { inboxesRef.current = inboxes; }, [inboxes]);

  const isInboxSoundEnabled = (provider?: string, externalAccountId?: string) => {
    const inbox = inboxesRef.current.find((item) => item.provider === provider && item.externalAccountId === externalAccountId);
    return inbox?.metadata?.soundNotification === true || inbox?.metadata?.soundNotification === 'true';
  };

  const playNotificationSound = () => {
    const now = Date.now();
    if (now - lastBeepAtRef.current < 1500) return; // evita rajada de beeps
    lastBeepAtRef.current = now;
    try {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;
      let ctx = notifyAudioCtxRef.current;
      if (!ctx) { ctx = new AudioContextClass(); notifyAudioCtxRef.current = ctx; }
      if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
      osc.start();
      osc.stop(ctx.currentTime + 0.34);
    } catch {
      // navegador sem audio disponivel
    }
  };

  // Badges de nao-lidas por inbox, derivados do estado local (sem fetch extra).
  const unreadByInbox = useMemo(() => {
    const map: Record<string, number> = {};
    for (const conv of allConversations) {
      const cnt = conv.unreadCount ?? 0;
      if (cnt > 0) {
        const k = `${conv.provider}:${conv.externalAccountId}`;
        map[k] = (map[k] || 0) + cnt;
      }
    }
    return map;
  }, [allConversations]);

  // Filtro e busca textual aplicados no cliente — troca de inbox e instantanea.
  const conversations = useMemo(() => {
    let list = allConversations;
    if (assignmentScope === 'mine') {
      list = list.filter((c) => assignedUserId(c) === user?.id);
    }
    if (selectedInboxKey !== 'all') {
      const [provider, ...rest] = selectedInboxKey.split(':');
      const accountId = rest.join(':');
      list = list.filter((c) => c.provider === provider && c.externalAccountId === accountId);
    }
    if (channelTypeFilter !== 'all') {
      list = list.filter((c) => c.channelType === channelTypeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        (c.contactName || '').toLowerCase().includes(q) ||
        (c.contactHandle || '').toLowerCase().includes(q) ||
        (c.contactPhone || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q)
      );
    }
    // A ordem final e SEMPRE derivada aqui, nao herdada da ordem em que os
    // itens entraram em allConversations: eventos em tempo real, patches por id
    // e acoes em massa alteram o estado sem reordenar, e a lista precisa
    // continuar coerente sem depender de um refetch para se arrumar.
    // Fixadas acima de tudo (escolha explicita do usuario).
    const at = (c: Conversation) => new Date(c.lastMessageAt || 0).getTime();
    list = [...list].sort((a, b) => {
      const pa = isConversationPinned(a) ? 1 : 0;
      const pb = isConversationPinned(b) ? 1 : 0;
      if (pa !== pb) return pb - pa;
      // Entre fixadas vale a ordem de fixacao (mais recente primeiro).
      if (pa && pb) {
        const ta = Number(new Date(String(a.metadata?.pinnedAt || 0)).getTime() || 0);
        const tb = Number(new Date(String(b.metadata?.pinnedAt || 0)).getTime() || 0);
        if (ta !== tb) return tb - ta;
      }
      if (sortMode === 'unread') {
        // stickyUnreadId: conversa que estava nao lida e voce acabou de abrir —
        // segue contando como nao lida para nao escorregar durante a leitura.
        const ua = (a.unreadCount ?? 0) > 0 || a.id === stickyUnreadId ? 1 : 0;
        const ub = (b.unreadCount ?? 0) > 0 || b.id === stickyUnreadId ? 1 : 0;
        if (ua !== ub) return ub - ua;
      }
      if (at(b) !== at(a)) return at(b) - at(a);
      return String(b.id).localeCompare(String(a.id));
    });
    return list;
  }, [allConversations, selectedInboxKey, search, assignmentScope, channelTypeFilter, sortMode, stickyUnreadId, user?.id]);

  // Mapa por id: a lista fazia chatFunnels.find() para CADA conversa renderizada
  // (O(n*m) a cada tecla digitada na resposta).
  const chatFunnelById = useMemo(() => new Map(chatFunnels.map((funnel) => [funnel.id, funnel])), [chatFunnels]);

  const availableChannelTypes = useMemo(() => {
    const set = new Set<string>();
    allConversations.forEach((c) => { if (c.channelType) set.add(c.channelType); });
    return Array.from(set);
  }, [allConversations]);

  const selected = useMemo(() => allConversations.find((item) => item.id === selectedId) || null, [allConversations, selectedId]);
  // Servidor + em voo, na ordem cronológica. As pendentes são sempre as mais
  // recentes, então entram no fim sem precisar reordenar a lista inteira.
  const visibleMessages = useMemo(() => [
    ...messages,
    ...pendingMessages.filter((item) => item.conversationId === selectedId).map((item) => item.message)
  ], [messages, pendingMessages, selectedId]);
  const isShopeeReplyDisabled = selected?.platform?.toLowerCase() === 'shopee'
    && selected.channelType !== 'comment'
    && !chatCapabilities.shopeeChatSendEnabled;
  // Janela de 24h fechada: mensagem livre nao chega no WhatsApp/Instagram/Facebook
  // oficial. Avisamos antes de digitar em vez de deixar o envio falhar calado.
  const sendWindow = selected?.sendWindow || null;
  // O compositor continua liberado de propósito: o backend recusa e devolve o
  // motivo, que fica registrado na conversa. Travar o campo esconderia o porquê.
  const isWindowClosed = Boolean(sendWindow?.applies && sendWindow?.open === false);
  // Template aprovado só existe no WhatsApp da API oficial — e a caixa da conta
  // ("account") é um placeholder de bootstrap, não uma conversa com alguém.
  const supportsOfficialTemplates = Boolean(
    selected
    && selected.provider === 'official'
    && selected.platform?.toLowerCase() === 'whatsapp'
    && selected.channelType !== 'account'
  );
  const selectedInbox = useMemo(() => inboxes.find((inbox) => `${inbox.provider}:${inbox.externalAccountId}` === selectedInboxKey) || null, [inboxes, selectedInboxKey]);
  const selectedConnection = selected?.connection || selectedInbox || null;
  const selectedConnectionHealth = selectedConnection?.connectionHealth || null;
  const chatCategories = useMemo(
    () => chatFunnels.flatMap((funnel) => (funnel.categories || []).map((category) => ({ category, funnel }))),
    [chatFunnels]
  );
  const funnelMoveCategories = useMemo(
    () => chatFunnels.find((funnel) => funnel.id === funnelMoveModal?.funnelId)?.categories || [],
    [chatFunnels, funnelMoveModal?.funnelId]
  );
  const visibleConversationIds = useMemo(() => conversations.map((conversation) => conversation.id), [conversations]);
  const selectedVisibleCount = useMemo(() => visibleConversationIds.filter((id) => selectedConversationIds.has(id)).length, [visibleConversationIds, selectedConversationIds]);
  const allVisibleSelected = visibleConversationIds.length > 0 && selectedVisibleCount === visibleConversationIds.length;

  const moveConversationContactToCategory = async (conversation: Conversation, categoriaId: string) => {
    if (!conversation.contactId || !categoriaId || pipelineSavingContactId) return;
    const target = chatCategories.find((item) => item.category.id === categoriaId);
    if (!target) {
      toast.error('Categoria nao encontrada no funil selecionado.');
      return;
    }
    setPipelineSavingContactId(conversation.contactId);
    try {
      const updated = await contactRequest<ContactDetails>(`/${conversation.contactId}`, {
        method: 'PATCH',
        body: JSON.stringify({ categoriaId })
      });
      const contactCategoria = {
        id: target.category.id,
        nome: target.category.nome,
        cor: target.category.cor,
        funnelId: target.funnel.id,
        funnel: { id: target.funnel.id, nome: target.funnel.nome, cor: target.funnel.cor }
      };
      setAllConversations((items) => items.map((item) => item.contactId === conversation.contactId
        ? { ...item, contactCategoriaId: categoriaId, contactCategoria }
        : item));
      if (contactDetails?.id === conversation.contactId) setContactDetails(updated);
      if (contactForm && contactDetails?.id === conversation.contactId) setContactForm({ ...contactForm, categoriaId });
      setFunnelMoveModal(null);
      toast.success(`Contato movido para ${target.funnel.nome} / ${target.category.nome}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao mover contato no funil');
    } finally {
      setPipelineSavingContactId(null);
    }
  };

  const openContactPanel = async () => {
    if (!selected) return;
    const requestId = ++contactLoadRequestRef.current;
    setContactPanelOpen(true);
    setContactEditing(false);
    setContactDetails(null);
    setContactForm(null);
    rawApiRequest<CategoryOption[] | { categories?: CategoryOption[] }>('/categorias/all').then((data) => setCategories(Array.isArray(data) ? data : (data.categories || []))).catch(() => undefined);
    if (!selected.contactId) {
      const socialField = socialFieldForPlatform(selected.platform);
      const socialHandle = socialHandleFromConversation(selected);
      setContactDetails(null);
      const nextForm: ContactEditForm = {
        nome: selected.contactName || selected.contactHandle || labelForConversation(selected) || '',
        email: '',
        endereco: '',
        cnpj: '',
        categoriaId: '',
        tags: [],
        followUpAtivo: false,
        observacoes: '',
        resumoIA: '',
        instagram: '',
        facebook: '',
        youtube: '',
        tiktok: '',
        twitter: '',
        linkedin: '',
        threads: '',
        bluesky: '',
        reddit: '',
        telegram: '',
        googleBusiness: ''
      };
      if (socialField && socialHandle) nextForm[socialField] = socialHandle;
      if (requestId === contactLoadRequestRef.current) {
        setContactForm(nextForm);
        setContactLoading(false);
      }
      return;
    }
    setContactLoading(true);
    try {
      const contact = await contactRequest<ContactDetails>(`/${selected.contactId}`);
      if (requestId !== contactLoadRequestRef.current) return;
      setContactDetails(contact);
      setContactForm(buildContactForm(contact));
    } catch (error) {
      if (requestId !== contactLoadRequestRef.current) return;
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar contato');
    } finally {
      if (requestId === contactLoadRequestRef.current) setContactLoading(false);
    }
  };

  useEffect(() => {
    if (!contactPanelOpen) return;
    if (!selected) {
      contactLoadRequestRef.current++;
      setContactPanelOpen(false);
      setContactEditing(false);
      setContactDetails(null);
      setContactForm(null);
      return;
    }
    setContactEditing(false);
    openContactPanel();
    // A troca deve reagir ao ID; openContactPanel usa a conversa selecionada
    // da renderizacao atual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const searchContacts = async () => {
    if (!contactSearch.trim()) return;
    try {
      const data = await contactRequest<{ contacts?: ContactSearchItem[] }>(`?search=${encodeURIComponent(contactSearch.trim())}&pageSize=10`);
      setContactResults(data.contacts || []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao buscar contatos');
    }
  };

  const linkExistingContact = async (contactId: string) => {
    if (!selected) return;
    try {
      await apiRequest(`/conversations/${selected.id}/link-contact`, { method: 'POST', body: JSON.stringify({ contactId }) });
      toast.success('Contato vinculado');
      await loadConversations();
      setSelectedId(selected.id);
      const contact = await contactRequest<ContactDetails>(`/${contactId}`);
      setContactDetails(contact);
      setContactForm(buildContactForm(contact));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao vincular contato');
    }
  };

  const createContactFromChat = async () => {
    if (!selected || !contactForm) return;
    try {
      const result = await apiRequest<{ contact: ContactDetails }>(`/conversations/${selected.id}/create-contact`, {
        method: 'POST',
        body: JSON.stringify(contactForm)
      });
      toast.success('Contato criado');
      await loadConversations();
      setContactDetails(result.contact);
      setContactForm(buildContactForm(result.contact));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar contato');
    }
  };

  const doToggleConversationAi = async (nextEnabled: boolean) => {
    if (!selected) return;
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${selected.id}/ai`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: nextEnabled })
      });
      setAllConversations((items) => items.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
      toast.success(nextEnabled ? 'IA ativada nesta conversa' : 'IA desativada nesta conversa');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar IA da conversa');
    }
  };

  const toggleConversationAi = async () => {
    if (!selected) return;
    const nextEnabled = !isConversationAiEnabled(selected);
    if (nextEnabled && (!isInboxAiEnabled(selectedConnection) || !isInboxChannelAiEnabled(selectedConnection, selected.channelType))) {
      toast.error('Para ativar a IA, ative primeiro a IA geral e o canal desta caixa');
      return;
    }
    // Créditos: ativar a IA gera custo por resposta automática. Confirma ao LIGAR; desligar é imediato.
    if (nextEnabled) {
      requestCreditConfirm({
        title: 'Ativar IA nesta conversa',
        items: [{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta da IA' }],
        note: 'Enquanto a IA estiver ativa, CADA resposta automática enviada ao cliente consome este valor. Sem saldo, a IA deixa de responder.',
        confirmLabel: 'Ativar IA',
        onConfirm: () => { doToggleConversationAi(true); },
      });
      return;
    }
    doToggleConversationAi(false);
  };

  const toggleConversationSelection = (conversationId: string, checked: boolean) => {
    setSelectedConversationIds((current) => {
      const next = new Set(current);
      if (checked) next.add(conversationId); else next.delete(conversationId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedConversationIds((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        for (const id of visibleConversationIds) next.delete(id);
      } else {
        for (const id of visibleConversationIds) next.add(id);
      }
      return next;
    });
  };

  const selectedVisibleConversations = () => conversations.filter((conversation) => selectedConversationIds.has(conversation.id));

  const runBulkConversationAction = async (action: 'delete' | 'read' | 'unread' | 'pin' | 'unpin') => {
    const targets = selectedVisibleConversations();
    if (!targets.length || bulkActionLoading) return;
    const labels: Record<typeof action, string> = {
      delete: 'apagar',
      read: 'marcar como lidas',
      unread: 'marcar como nao lidas',
      pin: 'fixar no topo',
      unpin: 'desafixar'
    };
    if (action === 'delete') {
      const ok = window.confirm(`Apagar ${targets.length} conversa(s) selecionada(s)? Essa acao remove o historico no CRM Chat.`);
      if (!ok) return;
    }
    setBulkActionLoading(true);
    try {
      if (action === 'delete') {
        for (const conversation of targets) await apiRequest(`/conversations/${conversation.id}`, { method: 'DELETE' });
        const targetIds = new Set(targets.map((item) => item.id));
        targetIds.forEach((id) => deletedConversationIdsRef.current.add(id));
        setAllConversations((items) => items.filter((item) => !targetIds.has(item.id)));
        if (selectedId && targetIds.has(selectedId)) {
          setSelectedId(null);
          setMessages([]);
        }
      } else {
        const updatedItems: Conversation[] = [];
        for (const conversation of targets) {
          if (action === 'read') updatedItems.push(await apiRequest<Conversation>(`/conversations/${conversation.id}/read`, { method: 'POST' }));
          if (action === 'unread') updatedItems.push(await apiRequest<Conversation>(`/conversations/${conversation.id}/unread`, { method: 'POST' }));
          if (action === 'pin') updatedItems.push(await apiRequest<Conversation>(`/conversations/${conversation.id}/pin`, { method: 'PATCH', body: JSON.stringify({ pinned: true }) }));
          if (action === 'unpin') updatedItems.push(await apiRequest<Conversation>(`/conversations/${conversation.id}/pin`, { method: 'PATCH', body: JSON.stringify({ pinned: false }) }));
        }
        const updatedMap = new Map(updatedItems.map((item) => [item.id, item]));
        setAllConversations((items) => sortConversationsPinnedFirst(items.map((item) => updatedMap.has(item.id) ? { ...item, ...updatedMap.get(item.id)! } : item)));
        if (currentTenant?.id && action === 'read') targets.forEach((conversation) => clearConversation(currentTenant.id, conversation.id));
      }
      setSelectedConversationIds(new Set());
      toast.success(`${targets.length} conversa(s): ${labels[action]}`);
      await loadConversations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao executar acao em massa');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const clearConversationAiHistory = async () => {
    if (!selected || clearingAiHistory) return;
    const ok = window.confirm('Apagar o historico de IA desta conversa?');
    if (!ok) return;
    setClearingAiHistory(true);
    try {
      await apiRequest(`/conversations/${selected.id}/ai-history`, { method: 'DELETE' });
      toast.success('Historico de IA apagado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao apagar historico de IA');
    } finally {
      setClearingAiHistory(false);
    }
  };

  const buildContactForm = (contact: ContactDetails): ContactEditForm => ({
    nome: contact.nome || '',
    email: contact.email || '',
    endereco: contact.endereco || '',
    cnpj: contact.cnpj || '',
    categoriaId: contact.categoriaId || '',
    tags: contact.tags || [],
    followUpAtivo: Boolean(contact.followUpAtivo),
    observacoes: contact.observacoes || '',
    resumoIA: contact.resumoIA || '',
    instagram: contact.instagram || '',
    facebook: contact.facebook || '',
    youtube: contact.youtube || '',
    tiktok: contact.tiktok || '',
    twitter: contact.twitter || '',
    linkedin: contact.linkedin || '',
    threads: contact.threads || '',
    bluesky: contact.bluesky || '',
    reddit: contact.reddit || '',
    telegram: contact.telegram || '',
    googleBusiness: contact.googleBusiness || ''
  });

  const saveContactPanel = async () => {
    if (!contactDetails || !contactForm) return;
    setContactSaving(true);
    try {
      const updated = await contactRequest<ContactDetails>(`/${contactDetails.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          nome: contactForm.nome,
          email: contactForm.email || null,
          endereco: contactForm.endereco || null,
          cnpj: contactForm.cnpj || null,
          categoriaId: contactForm.categoriaId || null,
          tags: contactForm.tags,
          followUpAtivo: contactForm.followUpAtivo,
          observacoes: contactForm.observacoes || null,
          resumoIA: contactForm.resumoIA || null,
          instagram: contactForm.instagram || null,
          facebook: contactForm.facebook || null,
          youtube: contactForm.youtube || null,
          tiktok: contactForm.tiktok || null,
          twitter: contactForm.twitter || null,
          linkedin: contactForm.linkedin || null,
          threads: contactForm.threads || null,
          bluesky: contactForm.bluesky || null,
          reddit: contactForm.reddit || null,
          telegram: contactForm.telegram || null,
          googleBusiness: contactForm.googleBusiness || null
        })
      });
      setContactDetails(updated);
      setContactForm(buildContactForm(updated));
      setContactEditing(false);
      toast.success('Contato atualizado');
      await loadConversations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar contato');
    } finally {
      setContactSaving(false);
    }
  };

  const loadInboxes = async () => {
    const data = await apiRequest<Inbox[]>('/inboxes');
    setInboxes(data);
  };

  const loadChatCapabilities = async () => {
    const data = await apiRequest<{ shopeeChatSendEnabled: boolean }>('/capabilities');
    setChatCapabilities({ shopeeChatSendEnabled: data.shopeeChatSendEnabled !== false });
  };

  const loadConversations = async (options: { append?: boolean } = {}) => {
    const append = Boolean(options.append);
    // Busca APLICADA (Enter), nao o texto em digitacao: enquanto o operador digita
    // a filtragem e local, e um refetch de fundo nao deve trocar a query do servidor.
    const searchTerm = appliedSearch.trim();

    const params = new URLSearchParams({ limit: String(CONVERSATION_PAGE_SIZE) });
    let scopedProvider = '';
    let scopedAccountId = '';
    if (selectedInboxKey !== 'all') {
      const [provider, ...rest] = selectedInboxKey.split(':');
      scopedProvider = provider;
      scopedAccountId = rest.join(':');
      params.set('provider', scopedProvider);
      params.set('externalAccountId', scopedAccountId);
    }
    if (searchTerm) params.set('search', searchTerm);
    if (sortMode === 'unread') params.set('sort', 'unread');

    // Query do servidor: inbox + busca aplicada + ordenacao. Se ela mudou, a
    // paginacao anterior nao vale mais (o cursor era daquele conjunto, nao deste).
    // Usa a busca APLICADA (Enter), nunca o texto sendo digitado: um refetch de
    // fundo no meio da digitacao zerava a paginacao de quem so estava filtrando
    // localmente.
    const queryKey = `${selectedInboxKey}|${searchTerm}|${sortMode}`;
    const queryChanged = !append && queryKey !== lastQueryKeyRef.current;
    if (append && paginationCursorRef.current) params.set('cursor', paginationCursorRef.current);
    // Sequencia: respostas fora de ordem (poll lento chegando depois de uma busca)
    // nao podem reescrever cursor/paginacao da query que esta na tela agora.
    const seq = ++listFetchSeqRef.current;

    const data = await apiRequest<Conversation[]>(`/conversations?${params.toString()}`);
    // O append e serializado pelo botao (loadingMoreConversations) e avanca a
    // paginacao por si: se ele fosse barrado por um refetch de pagina 1 que
    // chegou no meio, o cursor voltava para a pagina 1 e o "carregar mais"
    // passava a gastar cliques sem trazer nada.
    const isLatest = append || seq === listFetchSeqRef.current;

    if (isLatest && queryChanged) {
      lastQueryKeyRef.current = queryKey;
      pagesLoadedRef.current = 1;
      paginationCursorRef.current = null;
      listScrollTopRef.current = 0;
    }

    // Ordem do servidor (sem as fixadas, que ele so aplica no fim de cada pagina
    // e nao entram no cursor), usada para achar onde a pagina termina.
    // lastMessageAt nulo ordena PRIMEIRO, como o Postgres faz em DESC (NULLS
    // FIRST): tratar como epoch punha a linha no fim e ela era eleita fronteira,
    // sem data para virar cursor — o "carregar mais" ficava refazendo a pagina 1.
    const at = (item: Conversation) => (item.lastMessageAt ? new Date(item.lastMessageAt).getTime() : Number.POSITIVE_INFINITY);
    const serverOrder = (a: Conversation, b: Conversation) => {
      if (sortMode === 'unread') {
        const ua = (a.unreadCount ?? 0) > 0 ? 1 : 0;
        const ub = (b.unreadCount ?? 0) > 0 ? 1 : 0;
        if (ua !== ub) return ub - ua;
      }
      if (at(b) !== at(a)) return at(b) - at(a);
      return String(b.id).localeCompare(String(a.id));
    };
    // Fronteira = item da resposta que ordena por ultimo (entre os que tem data,
    // que sao os unicos que servem de cursor). E dele que sai o proximo cursor.
    // Antes o cursor era derivado do item mais antigo em memoria: qualquer
    // conversa antiga injetada por evento ou deep-link envenenava a paginacao e as
    // paginas do meio ficavam inalcancaveis.
    const boundary = data
      .filter((item) => item.lastMessageAt && item.id)
      .reduce<Conversation | null>((worst, item) => (!worst || serverOrder(item, worst) > 0 ? item : worst), null);
    if (isLatest) {
      if (append) pagesLoadedRef.current += 1;
      if (boundary?.lastMessageAt && boundary.id && (append || pagesLoadedRef.current <= 1)) {
        const group = sortMode === 'unread' ? ((boundary.unreadCount ?? 0) > 0 ? '|u' : '|r') : '';
        // Um refetch da pagina 1 nao pode encurtar a paginacao de quem ja carregou
        // mais paginas: nesse caso o cursor mais profundo continua valendo.
        paginationCursorRef.current = `${boundary.lastMessageAt}|${boundary.id}${group}`;
      }
      if (append || pagesLoadedRef.current <= 1) setHasMoreConversations(data.length === CONVERSATION_PAGE_SIZE);
    }

    setAllConversations((current) => {
      // Merge preservando o que ja estava carregado. Antes, um refetch da pagina 1
      // (poll de 30s, chegada de mensagem, envio, busca...) reconstruia a lista a
      // partir dos 60 primeiros e jogava fora tudo que veio do "carregar mais": a
      // lista encolhia embaixo do usuario e o scroll era clampado de volta ao topo.
      //
      // A reconciliacao (deixar o servidor remover o que nao existe mais) so roda
      // no caso INEQUIVOCO: pagina 1 sozinha, sem paginacao por cima e sem busca
      // no meio — que era exatamente o comportamento antigo. Quando ha mais
      // paginas carregadas, preferimos uma linha desatualizada a fazer conversa
      // que existe desaparecer da tela: podar ali dependia de comparar item em
      // memoria (que o stream pode ter acabado de atualizar) com a fronteira da
      // resposta, e isso escondia conversa que tinha recebido mensagem durante o
      // proprio fetch. Ela se resolve sozinha ao trocar de caixa/ordenacao, que
      // recarrega a pagina 1 limpa.
      // Vale tambem quando a query acabou de mudar (o reset acima ja deixou
      // pagesLoaded em 1): e assim que limpar a busca descarta os resultados
      // antigos e voltar de uma caixa nao deixa os badges somando conversas de
      // uma janela mais funda que as outras caixas.
      const reconcile = !append && isLatest && pagesLoadedRef.current <= 1
        && !searchTerm && data.length > 0;
      const dataIds = new Set(data.map((item) => item.id));
      const kept = !reconcile ? current : current.filter((item) => {
        if (dataIds.has(item.id)) return true;
        if (item.id === selectedId || directlyLoadedConversationIdsRef.current.has(item.id)) return true;
        // Fora do escopo desta request (outra caixa): o servidor nao opinou.
        if (selectedInboxKey !== 'all' && !(item.provider === scopedProvider && item.externalAccountId === scopedAccountId)) return true;
        // Resposta menor que a pagina = e o conjunto completo deste escopo, logo o
        // que nao voltou nao existe mais (apagada, caixa removida ou oculta).
        if (data.length < CONVERSATION_PAGE_SIZE) return false;
        // Mais antiga que a fronteira: fora da janela que a resposta cobre.
        return boundary ? serverOrder(item, boundary) > 0 : false;
      });
      const merged = new Map(kept.map((item) => [item.id, item]));
      for (const item of data) merged.set(item.id, { ...merged.get(item.id), ...item });
      for (const id of deletedConversationIdsRef.current) merged.delete(id);
      return Array.from(merged.values());
    });
    // Nao feche uma conversa carregada diretamente so porque ela ficou fora da
    // primeira pagina da listagem.
  };

  const loadMoreConversations = async () => {
    if (loadingMoreConversations || !hasMoreConversations) return;
    setLoadingMoreConversations(true);
    try {
      await loadConversations({ append: true });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar mais conversas');
    } finally {
      setLoadingMoreConversations(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    const data = await apiRequest<{ conversation?: Conversation; messages: Message[] }>(`/conversations/${conversationId}/messages`);
    if (data.conversation) setAllConversations((items) => items.map((item) => item.id === conversationId ? { ...item, ...data.conversation } : item));
    setMessages(data.messages || []);
  };

  const markConversationUnread = async (conversation: Conversation, event?: ReactMouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setConvMenuId(null);
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${conversation.id}/unread`, { method: 'POST' });
      setAllConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, ...updated } : item));
      // Se a conversa marcada estava aberta, fecha para nao re-zerar automaticamente.
      if (selectedId === conversation.id) setSelectedId(null);
      toast.success('Conversa marcada como nao lida');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao marcar como nao lida');
    }
  };

  const markConversationReadFromMenu = async (conversation: Conversation, event?: ReactMouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setConvMenuId(null);
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${conversation.id}/read`, { method: 'POST' });
      setAllConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, ...updated } : item));
      if (currentTenant?.id) clearConversation(currentTenant.id, conversation.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao marcar como lida');
    }
  };

  const toggleConversationPinned = async (conversation: Conversation, event?: ReactMouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setConvMenuId(null);
    const nextPinned = !isConversationPinned(conversation);
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${conversation.id}/pin`, {
        method: 'PATCH',
        body: JSON.stringify({ pinned: nextPinned })
      });
      setAllConversations((items) => {
        const next = items.map((item) => item.id === conversation.id ? { ...item, ...updated } : item);
        return sortConversationsPinnedFirst(next);
      });
      toast.success(nextPinned ? 'Conversa fixada no topo' : 'Conversa desafixada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao fixar conversa');
    }
  };

  const loadUsers = async () => {
    const data = await rawApiRequest<{ users?: CrmUserOption[]; data?: { users?: CrmUserOption[] } }>('/users?pageSize=100&ativo=true');
    const list = data.users || data.data?.users || [];
    setUsers(list.filter((item) => item.ativo !== false));
  };

  const openAssignModal = () => {
    if (!selected) return;
    setAssignModalOpen(true);
    loadUsers().catch((error) => toast.error(error instanceof Error ? error.message : 'Erro ao carregar usuarios'));
  };

  const assignConversationTo = async (assignedUserIdValue: string | null) => {
    if (!selected || assigningConversation) return;
    setAssigningConversation(true);
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${selected.id}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assignedUserId: assignedUserIdValue })
      });
      setAllConversations((items) => items.map((item) => item.id === updated.id ? { ...item, ...updated } : item));
      setAssignModalOpen(false);
      toast.success(assignedUserIdValue ? 'Conversa atribuida' : 'Atribuicao removida');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atribuir conversa');
    } finally {
      setAssigningConversation(false);
    }
  };

  const doToggleInboxAi = async (inbox: Inbox, nextEnabled: boolean) => {
    setSavingInboxAiId(inbox.id);
    try {
      const updated = await apiRequest<Inbox>(`/inboxes/${inbox.id}/ai`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: nextEnabled })
      });
      setInboxes((items) => items.map((item) => item.id === inbox.id ? { ...item, ...updated } : item));
      setAllConversations((items) => items.map((item) => {
        if (item.connection?.id !== inbox.id) return item;
        return { ...item, connection: { ...item.connection, ...updated } };
      }));
      toast.success(nextEnabled ? 'IA ativada na caixa de entrada' : 'IA desativada na caixa de entrada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar IA da caixa');
    } finally {
      setSavingInboxAiId(null);
    }
  };

  const toggleInboxAiFromCard = async (inbox: Inbox, event?: ReactMouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (savingInboxAiId) return;
    const nextEnabled = !isInboxAiEnabled(inbox);
    // Créditos: ligar a IA da caixa gera custo por resposta automática. Confirma ao LIGAR.
    if (nextEnabled) {
      requestCreditConfirm({
        title: 'Ativar IA nesta caixa de entrada',
        items: [{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta da IA' }],
        note: 'Enquanto ativa, CADA resposta automática da IA às mensagens desta caixa consome este valor. Sem saldo, a IA deixa de responder.',
        confirmLabel: 'Ativar IA',
        onConfirm: () => { doToggleInboxAi(inbox, true); },
      });
      return;
    }
    doToggleInboxAi(inbox, false);
  };

  const loadScheduledMessages = async (conversationId: string) => {
    const data = await apiRequest<ScheduledMessage[]>(`/conversations/${conversationId}/scheduled-messages`);
    setScheduledMessages(data || []);
  };

  // Reuniões futuras desta conversa (badge no cabeçalho)
  const loadConversationMeetings = async (conversationId: string) => {
    const data = await meetingFetch(`/api/meetings/conversation/${conversationId}`);
    if (selectedIdRef.current !== conversationId) return;
    const list: ExistingMeeting[] = Array.isArray(data.meetings) ? data.meetings : [];
    setConversationMeetings(list.filter((m) => m.status !== 'CANCELLED' && m.status !== 'DONE' && m.status !== 'NO_SHOW' && new Date(m.startAt).getTime() >= Date.now() - 3600000));
  };

  const refreshAvatar = async (conversationId: string) => {
    try {
      const updated = await apiRequest<Conversation>(`/conversations/${conversationId}/refresh-avatar`, { method: 'POST' });
      setAllConversations((items) => items.map((item) => item.id === conversationId ? { ...item, ...updated } : item));
    } catch {
      // silencioso: nem todo contato tem foto disponivel (ex.: LID sem telefone)
    }
  };

  useEffect(() => {
    loadInboxes().catch((error) => toast.error(error.message));
    loadChatCapabilities().catch(() => undefined);
    apiService.getFunnels({ includeCategories: true })
      .then(setChatFunnels)
      .catch(() => setChatFunnels([]));
  }, []);

  useEffect(() => {
    const refreshCapabilities = () => loadChatCapabilities().catch(() => undefined);
    window.addEventListener('focus', refreshCapabilities);
    return () => window.removeEventListener('focus', refreshCapabilities);
  }, []);

  useEffect(() => {
    // Saúde das caixas muda sem necessariamente chegar uma mensagem no CRM.
    // Atualize só a lista leve de caixas para o alerta aparecer rapidamente.
    const refreshInboxHealth = () => {
      if (document.visibilityState === 'visible') loadInboxes().catch(() => undefined);
    };
    const interval = window.setInterval(refreshInboxHealth, 15_000);
    window.addEventListener('focus', refreshInboxHealth);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshInboxHealth);
    };
  }, []);

  useEffect(() => {
    // O reset da paginacao (cursor, paginas, scroll) e feito dentro do
    // loadConversations, comparando a queryKey — vale para qualquer caminho que
    // mude inbox, busca ou ordenacao, nao so para este effect.
    loadConversations().catch((error) => toast.error(error.message));
  }, [selectedInboxKey, sortMode, appliedSearch]);

  useEffect(() => { localStorage.setItem('crm_chat_sort_mode', sortMode); }, [sortMode]);

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // Volta a lista para onde estava. Em telas <md o <aside> e escondido ao abrir a
  // conversa, o que zera o scrollTop no browser; em telas grandes isso e um no-op.
  useLayoutEffect(() => {
    const el = listScrollRef.current;
    if (!el || listScrollTopRef.current <= 0) return;
    if (el.scrollTop === 0) el.scrollTop = listScrollTopRef.current;
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setGroupInfoOpen(false);
      // Fechar a conversa libera o congelamento na faixa das nao lidas; sem isto
      // ela ficava grudada acima de nao lidas de verdade ate abrir outra.
      setStickyUnreadId(null);
      return;
    }
    setGroupInfoOpen(false);
    loadMessages(selectedId).catch((error) => toast.error(error.message));
    loadScheduledMessages(selectedId).catch(() => undefined);
    loadConversationMeetings(selectedId).catch(() => undefined);
    // allConversations, nao a lista filtrada: com um filtro de tela ativo
    // (atribuidas a mim, tipo de canal, busca) a conversa aberta podia nao estar
    // na lista visivel e entao NUNCA era marcada como lida no servidor.
    const current = allConversations.find((item) => item.id === selectedId);
    if (current && !avatarUrlForConversation(current)) refreshAvatar(selectedId);
    // Na ordenacao "nao lidas primeiro", ler a conversa a tiraria do grupo de
    // cima no meio da leitura. Ela fica congelada na faixa das nao lidas ate
    // voce sair dela — mas SO se ja estava nao lida: uma conversa antiga que
    // voce abriu la embaixo tem de continuar la embaixo, nao saltar pro topo.
    setStickyUnreadId((current?.unreadCount ?? 0) > 0 ? selectedId : null);
    // `!current` = conversa aberta por deep-link que ainda nao entrou na lista:
    // marcar como lido de qualquer jeito, senao o badge dela nunca zera. O
    // backend ignora o pedido quando ja esta zerada (nem emite evento).
    if (!current || (current.unreadCount ?? 0) > 0) {
      // Zera o unreadCount localmente ja (badge some na hora, sem esperar o servidor).
      setAllConversations((items) => items.map((item) => item.id === selectedId ? { ...item, unreadCount: 0 } : item));
      apiRequest<Conversation>(`/conversations/${selectedId}/read`, { method: 'POST' })
        .then((updated) => {
          setAllConversations((items) => items.map((item) => item.id === selectedId ? { ...item, ...updated } : item));
          if (currentTenant?.id) clearConversation(currentTenant.id, selectedId);
        })
        .catch(() => undefined);
    } else if (currentTenant?.id) {
      clearConversation(currentTenant.id, selectedId);
    }
  }, [selectedId]);

  const forceScrollToBottomRef = useRef(false);
  useEffect(() => {
    // Trocar de conversa volta ao modo "seguir o fim". Nao basta rolar uma vez no
    // clique: as mensagens chegam depois (fetch), e nesse render o conteudo ainda
    // era o da conversa anterior.
    followBottomRef.current = true;
  }, [selectedId]);
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    // Acompanha o fim enquanto o operador estiver no fim (followBottomRef, mantido
    // pelo onScroll do painel). Quem subiu para procurar algo no historico nao e
    // arrastado para baixo porque chegou mensagem ou porque o reload trocou a
    // lista; o proprio envio (forceScroll) sempre desce.
    const forced = forceScrollToBottomRef.current;
    forceScrollToBottomRef.current = false;
    if (!followBottomRef.current && !forced) return;
    window.requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    // visibleMessages e nao messages: a bolha em voo tambem precisa rolar para a
    // vista, senao o operador nao ve o proprio envio aparecer.
    //
    // O id da ultima mensagem entra nas deps junto com o total: getMessages corta
    // em 80, entao duas conversas longas tem o MESMO length e, so pelo length, o
    // efeito nao rodava quando as mensagens da conversa nova chegavam — o painel
    // abria no meio do historico.
  }, [selectedId, visibleMessages.length, visibleMessages[visibleMessages.length - 1]?.id]);

  // Polling apenas como fallback do SSE; pausa quando a aba esta em segundo plano
  // para nao desperdiciar requests/banco enquanto ninguem esta olhando.
  const pollRef = useRef<() => void>(() => undefined);
  const pollTickRef = useRef(0);
  useEffect(() => {
    pollRef.current = () => {
      if (document.visibilityState === 'hidden') return;
      pollTickRef.current += 1;
      // Com o stream ativo a lista chega por evento, e refazer o GET a cada 30s
      // era trabalho repetido. Mas nao da para calar de vez: a conversa ABERTA
      // precisa do sendWindow (janela de 24h) e dos status de mensagem, que nao
      // vem por evento; e etiqueta/etapa do contato, snapshot da caixa em cada
      // conversa e conversas removidas sem evento so se corrigem com a listagem.
      // Entao: conversa aberta a cada tique, sincronizacao completa a cada 5.
      const streaming = streamConnectedRef.current;
      if (streaming && pollTickRef.current % 5 !== 0) {
        if (selectedId) loadMessages(selectedId).catch(() => undefined);
        return;
      }
      loadInboxes().catch(() => undefined);
      loadChatCapabilities().catch(() => undefined);
      loadConversations().catch(() => undefined);
      if (selectedId) loadMessages(selectedId).catch(() => undefined);
    };
  });
  useEffect(() => {
    // O intervalo e criado uma vez: antes ele era recriado (e o contador zerado)
    // a cada tecla da busca, entao o fallback praticamente nunca disparava.
    const interval = window.setInterval(() => pollRef.current(), 30000);
    return () => window.clearInterval(interval);
  }, []);

  // Agrupa rajadas de eventos em um unico refetch de listas (em vez de um por mensagem).
  const scheduleListReload = () => {
    if (reloadTimerRef.current) return;
    reloadTimerRef.current = window.setTimeout(() => {
      reloadTimerRef.current = null;
      loadInboxes().catch(() => undefined);
      loadConversations().catch(() => undefined);
    }, 400);
  };
  const scheduleMessagesReload = (conversationId: string) => {
    if (msgReloadTimerRef.current) window.clearTimeout(msgReloadTimerRef.current);
    msgReloadTimerRef.current = window.setTimeout(() => {
      msgReloadTimerRef.current = null;
      loadMessages(conversationId).catch(() => undefined);
    }, 250);
  };

  const handleCrmChatEvent = (payload: any) => {
    try {
      if (payload.type === 'crm_chat_message') {
        // O evento ja traz a conversa atualizada (lastMessageAt, unreadCount):
        // basta aplicar o patch. Antes isto refazia GET /conversations +
        // GET /inboxes a cada mensagem do tenant, e era esse refetch que
        // truncava a lista e derrubava o scroll de quem estava lendo o historico.
        // O refetch fica so para conversa que ainda nao existe em memoria, que
        // precisa dos dados derivados da listagem (caixa, etiquetas, funil).
        if (payload.conversation?.id && deletedConversationIdsRef.current.has(payload.conversation.id)) return;
        if (payload.conversation?.id) {
          // O `some` roda DENTRO do updater (estado mais novo): varios eventos no
          // mesmo chunk do stream sao processados no mesmo tick, e decidir por
          // fora, com o estado do ultimo render, duplicava a linha.
          setAllConversations((items) => (items.some((item) => item.id === payload.conversation.id)
            ? items.map((item) => item.id === payload.conversation.id
              ? { ...item, ...payload.conversation, lastMessage: payload.message || item.lastMessage }
              : item)
            : items));
        }
        // Desconhecida aqui = precisa dos campos que so a listagem monta.
        if (!payload.conversation?.id || !allConversations.some((item) => item.id === payload.conversation.id)) {
          scheduleListReload();
        }
        // Toca aviso sonoro para mensagens recebidas, se a caixa tiver o som ativado.
        if (payload.message?.direction === 'incoming'
          && isInboxSoundEnabled(payload.conversation?.provider, payload.conversation?.externalAccountId)) {
          playNotificationSound();
        }
        if (selectedId && payload.conversation?.id === selectedId) {
          scheduleMessagesReload(selectedId);
          // Conversa esta aberta: zera nao-lidas automaticamente
          if (payload.message?.direction === 'incoming') {
            apiRequest<Conversation>(`/conversations/${selectedId}/read`, { method: 'POST' })
              .then((updated) => setAllConversations((items) => items.map((item) => item.id === selectedId ? { ...item, ...updated } : item)))
              .catch(() => undefined);
          }
        }
      }
      if (payload.type === 'crm_chat_conversation' && payload.conversation?.id) {
        if (deletedConversationIdsRef.current.has(payload.conversation.id)) return;
        // Decisao de inserir vs. atualizar DENTRO do updater: eventos do mesmo
        // chunk do stream rodam no mesmo tick e, decidindo por fora, a mesma
        // conversa era inserida duas vezes (duas linhas com a mesma key).
        setAllConversations((items) => (items.some((item) => item.id === payload.conversation.id)
          ? items.map((item) => item.id === payload.conversation.id ? { ...item, ...payload.conversation } : item)
          // Conversa ainda nao carregada nesta aba (ex.: acabou de ser criada ou
          // ficou fora da primeira pagina): insere em vez de descartar o evento.
          : [payload.conversation, ...items]));
        // O evento traz a linha crua do banco, sem os campos que so a listagem
        // monta (caixa, ultima mensagem, etiquetas, funil). Pede a listagem para
        // o card nao ficar sem conexao e com "Sem mensagens".
        if (!allConversations.some((item) => item.id === payload.conversation.id)) scheduleListReload();
      }
      if (payload.type === 'crm_chat_inbox' && payload.inbox?.id) {
        setInboxes((items) => items.map((item) => item.id === payload.inbox.id ? { ...item, ...payload.inbox } : item));
      }
      if (payload.type === 'crm_chat_conversation_deleted') {
        if (payload.conversationId) deletedConversationIdsRef.current.add(payload.conversationId);
        setAllConversations((current) => current.filter((conversation) => conversation.id !== payload.conversationId));
        if (selectedId === payload.conversationId) {
          setSelectedId(null);
          setMessages([]);
        }
      }
    } catch {
      // Evento invalido nao deve quebrar o chat.
    }
  };

  // O handler fecha sobre estado que muda a cada tecla (busca, conversa aberta).
  // Guardar a versao mais nova num ref deixa a conexao SSE abaixo abrir uma unica
  // vez: antes ela era derrubada e reaberta a cada digito, e os eventos que
  // chegavam nessa janela eram perdidos — dai a necessidade de dar refresh.
  const crmChatEventRef = useRef(handleCrmChatEvent);
  const crmChatResyncRef = useRef(scheduleListReload);
  useEffect(() => {
    crmChatEventRef.current = handleCrmChatEvent;
    crmChatResyncRef.current = scheduleListReload;
  });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const controller = new AbortController();
    let reconnectTimer: number | null = null;
    let closed = false;

    let attempt = 0;
    let streaming = false;

    const connect = async () => {
      if (closed || streaming) return;
      streaming = true;
      try {
        const response = await fetch('/api/crm-chat/events', {
          headers: authHeaders() as HeadersInit,
          signal: controller.signal
        });
        if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
        // Reconexao: houve uma janela sem eventos, entao ressincroniza as listas.
        if (attempt > 0) crmChatResyncRef.current();
        attempt = 0;
        streamConnectedRef.current = true;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!closed) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.trim()) continue;
            crmChatEventRef.current(JSON.parse(line));
          }
        }
      } catch {
        // Reconecta no finally.
      } finally {
        streaming = false;
        streamConnectedRef.current = false;
        attempt += 1;
        // Backoff curto e limitado: 1s, 2s, 4s... ate 15s.
        if (!closed) reconnectTimer = window.setTimeout(connect, Math.min(1000 * 2 ** (attempt - 1), 15000));
      }
    };

    // Aba volta ao foco ou rede volta: reconecta na hora em vez de esperar o backoff.
    const wakeUp = () => {
      if (closed || streaming || document.visibilityState === 'hidden') return;
      if (reconnectTimer) { window.clearTimeout(reconnectTimer); reconnectTimer = null; }
      connect();
    };
    document.addEventListener('visibilitychange', wakeUp);
    window.addEventListener('online', wakeUp);

    connect();
    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', wakeUp);
      window.removeEventListener('online', wakeUp);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (reloadTimerRef.current) { window.clearTimeout(reloadTimerRef.current); reloadTimerRef.current = null; }
      if (msgReloadTimerRef.current) { window.clearTimeout(msgReloadTimerRef.current); msgReloadTimerRef.current = null; }
      controller.abort();
    };
  }, []);

  const uploadAttachment = async (file: File) => {
    // Um anexo por vez. Sem isto, gravar/anexar outro áudio com um ainda subindo
    // fazia os dois uploads correrem juntos e o último a responder sobrescrevia
    // `attachment` — o operador mandava um áudio e ia embora o outro.
    if (uploadingRef.current) {
      toast.error('Aguarde o anexo atual terminar de subir.');
      return;
    }
    uploadingRef.current = true;
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    // Upload sem timeout ficava "Anexando..." para sempre quando a rede ou o
    // servidor engasgava, sem nenhuma saída para o operador.
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 120000);
    try {
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: authHeaders() as HeadersInit,
        body: formData,
        signal: controller.signal
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || 'Erro ao anexar arquivo');
      const uploaded = {
        url: data.fileUrl,
        filename: data.originalName || data.filename || file.name,
        contentType: data.mimetype || file.type,
        type: data.mimetype || file.type
      };
      setAttachment(uploaded);
      return uploaded;
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === 'AbortError';
      toast.error(aborted
        ? 'O anexo demorou demais e foi cancelado. Tente novamente.'
        : (error instanceof Error ? error.message : 'Erro ao anexar arquivo'));
    } finally {
      window.clearTimeout(timeout);
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  // O AudioContext da gravação anterior ficava aberto para sempre: cada gravação
  // deixava uma thread de áudio viva no navegador, e a página inteira ia ficando
  // pesada ao longo do expediente. Guardar o contexto é o que permite fechá-lo.
  const audioContextRef = useRef<AudioContext | null>(null);

  const stopWaveform = () => {
    if (analyserFrameRef.current) window.cancelAnimationFrame(analyserFrameRef.current);
    analyserFrameRef.current = null;
    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    if (audioContext && audioContext.state !== 'closed') audioContext.close().catch(() => undefined);
  };

  const startWaveform = (stream: MediaStream) => {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    audioContextRef.current = audioContext;
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    analyser.fftSize = 128;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    // `recordingSamples` mora no estado desta página, que é um componente único e
    // gigante: um setState por quadro (~60/s) re-renderizava a lista de conversas
    // e o histórico inteiro 60 vezes por segundo enquanto o operador gravava — era
    // isso que fazia "travar tudo" ao gravar áudio. 12 quadros por segundo é mais
    // que suficiente para a onda parecer viva.
    const FRAME_INTERVAL_MS = 80;
    let lastPaintedAt = 0;
    const tick = (now: number) => {
      analyserFrameRef.current = window.requestAnimationFrame(tick);
      if (now - lastPaintedAt < FRAME_INTERVAL_MS) return;
      lastPaintedAt = now;
      analyser.getByteFrequencyData(data);
      const next = Array.from({ length: 28 }, (_, index) => {
        const start = Math.floor(index * data.length / 28);
        const end = Math.max(start + 1, Math.floor((index + 1) * data.length / 28));
        const avg = data.slice(start, end).reduce((sum, item) => sum + item, 0) / (end - start);
        return Math.max(0.12, Math.min(1, avg / 120));
      });
      setRecordingSamples(next);
    };
    analyserFrameRef.current = window.requestAnimationFrame(tick);
  };

  // Sair da página no meio de uma gravação também tem que liberar o contexto.
  useEffect(() => () => stopWaveform(), []);

  const startRecording = async () => {
    // Gravar por cima de um envio em andamento embaralhava tudo: o `attachment`
    // era trocado no meio do caminho e o áudio anterior ia sem dono ou não ia.
    if (uploadingRef.current || sending) {
      toast.error('Aguarde o áudio anterior terminar de ser enviado.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Negocia o formato da gravação em vez de aceitar o padrão do navegador.
      //
      // `new MediaRecorder(stream)` sem opções deixa o Chrome escolher, e ele
      // rotula o arquivo pelo CONTÊINER: a gravação saía como "video/webm" mesmo
      // sendo só áudio. No servidor isso caía no pipeline de VÍDEO, que faz conta
      // de bitrate por duração e recusava um áudio longo com "Vídeo longo demais
      // para o limite de 16 MB". Pedindo explicitamente um tipo audio/*, isso não
      // acontece mais; a ordem prioriza o que o WhatsApp aceita sem conversão.
      const preferredMimeTypes = [
        'audio/ogg;codecs=opus',   // Firefox: já sai no formato de mensagem de voz
        'audio/mp4',               // Safari: aceito pelo WhatsApp como está
        'audio/webm;codecs=opus',  // Chrome/Edge: precisa de conversão no servidor
        'audio/webm'
      ];
      const supportedMimeType = typeof MediaRecorder.isTypeSupported === 'function'
        ? preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type))
        : undefined;
      const recorder = supportedMimeType
        ? new MediaRecorder(stream, { mimeType: supportedMimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        stopWaveform();
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        // A extensão TEM que bater com o que o navegador realmente gravou.
        // Safari (iPhone/Mac) grava MP4/AAC, não WebM: o nome fixo ".webm" fazia
        // o arquivo ser servido como video/webm, e aí o próprio Safari se recusava
        // a tocar o preview — e a Meta recusava o envio com o código 131053,
        // porque WebM não é formato aceito pelo WhatsApp.
        // Rede de segurança: se mesmo assim vier um rótulo de vídeo (navegador
        // antigo ignorando o mimeType pedido), força audio/* — o conteúdo é uma
        // gravação de microfone, não tem faixa de vídeo nenhuma.
        const rawMimeType = blob.type || recorder.mimeType || 'audio/webm';
        const mimeType = rawMimeType.startsWith('video/')
          ? rawMimeType.replace('video/', 'audio/')
          : rawMimeType;
        const file = new File([blob], `audio-${Date.now()}.${audioExtensionForMime(mimeType)}`, { type: mimeType });
        const uploaded = await uploadAttachment(file);
        if (sendAfterRecordingRef.current) {
          sendAfterRecordingRef.current = false;
          if (uploaded) sendPayload('', uploaded);
        }
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      setRecordingSamples(Array(28).fill(0.15));
      startWaveform(stream);
    } catch (error) {
      toast.error('Nao foi possivel acessar o microfone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
    setMediaRecorder(null);
    setRecording(false);
  };

  const discardRecording = () => {
    if (recording) stopRecording();
    streamRef.current?.getTracks().forEach((track) => track.stop());
    stopWaveform();
    setRecording(false);
    setMediaRecorder(null);
    setAttachment(null);
    chunksRef.current = [];
  };

  const send = async () => {
    if (isShopeeReplyDisabled) return;
    if (recording) {
      sendAfterRecordingRef.current = true;
      stopRecording();
      return;
    }
    return sendPayload(draft.trim(), attachment);
  };

  const sendPayload = async (body: string, payloadAttachment: ChatAttachment | null) => {
    if (!selectedId || sending || isShopeeReplyDisabled || (!body && !payloadAttachment)) return;
    const attachments = payloadAttachment ? [payloadAttachment] : [];
    const replyToExternalId = replyTo?.externalMessageId || null;
    // Sufixo aleatório: dois envios no mesmo milissegundo geravam o mesmo id e um
    // removia a bolha do outro.
    const optimisticId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const conversationId = selectedId;

    // A mensagem em voo vive numa lista SEPARADA da do servidor.
    //
    // Antes ela era empurrada no mesmo array `messages`, e qualquer recarga que
    // acontecesse durante o envio (o polling de 30s, um evento de websocket da
    // conversa) trocava o array inteiro e a bolha desaparecia — com áudio e vídeo
    // ficava evidente, porque o envio demora mais (conversão + upload) e o
    // operador achava que a mídia não tinha sido enviada.
    // O proprio envio sempre desce para o fim, mesmo que o operador estivesse
    // com o historico rolado para cima.
    forceScrollToBottomRef.current = true;
    setPendingMessages((current) => [...current, {
      id: optimisticId,
      conversationId,
      message: {
        id: optimisticId,
        direction: 'outgoing',
        senderName: 'Voce',
        body,
        messageType: attachments[0]?.type || attachments[0]?.contentType || (body ? 'text' : 'file'),
        attachments,
        sentAt: new Date().toISOString(),
        replyToExternalId,
        status: 'sending'
      }
    }]);
    setDraft('');
    setAttachment(null);
    setReplyTo(null);
    setSending(true);
    try {
      const sent = await apiRequest<Message>(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          body,
          attachments,
          replyToExternalId
        })
      });
      // Entra na lista do servidor ANTES de sair da lista de pendentes, para não
      // existir um quadro em que a mensagem não aparece em nenhuma das duas.
      if (conversationId === selectedIdRef.current) {
        setMessages((current) => current.some((message) => message.id === sent?.id) ? current : [...current, sent]);
      }
      setPendingMessages((current) => current.filter((item) => item.id !== optimisticId));
      await loadConversations();
    } catch (error) {
      setPendingMessages((current) => current.filter((item) => item.id !== optimisticId));
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar mensagem', { duration: 8000 });
      // O backend registra a tentativa recusada como mensagem "failed": recarregar
      // faz o aviso ficar na conversa (o toast desaparece) e atualiza a janela de 24h.
      loadMessages(conversationId).catch(() => undefined);
    } finally {
      setSending(false);
    }
  };

  // Áudio já gerado (e já cobrado) pelo modal de voz clonada: entra no fluxo
  // normal de anexo — o backend converte para ogg/opus (WAHA) ou m4a (oficial)
  // na hora do envio, como em qualquer outro áudio.
  const sendVoiceAudio = async (audio: GeneratedVoiceAudio) => {
    await sendPayload('', {
      url: audio.url,
      type: 'audio',
      contentType: audio.contentType,
      filename: audio.filename || 'voz.mp3'
    } as ChatAttachment);
  };

  const reloadVoiceClones = async () => {
    try {
      setVoiceClones(await fetchVoiceClones());
    } catch {
      // Silencioso: a ausência do catálogo só desabilita o botão de voz clonada.
    }
  };

  useEffect(() => { reloadVoiceClones(); }, []);

  const sendPayloadDirect = async (body: string, payloadAttachment: ChatAttachment | null) => {
    if (!selectedId || isShopeeReplyDisabled || (!body && !payloadAttachment)) return null;
    const attachments = payloadAttachment ? [payloadAttachment] : [];
    return apiRequest<Message>(`/conversations/${selectedId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, attachments, replyToExternalId: null })
    });
  };

  const deleteConversation = async (conversation: Conversation, event?: { stopPropagation: () => void }) => {
    event?.stopPropagation();
    const confirmed = window.confirm(`Apagar a conversa com ${labelForConversation(conversation)}? Essa acao remove o historico desta conversa no CRM Chat.`);
    if (!confirmed) return;

    setLoading(true);
    try {
      await apiRequest(`/conversations/${conversation.id}`, { method: 'DELETE' });
      toast.success('Conversa apagada');
      const deletingSelected = conversation.id === selectedId;
      if (deletingSelected) {
        setMessages([]);
        setReplyTo(null);
        setDraft('');
        setAttachment(null);
        setContactPanelOpen(false);
        setContactDetails(null);
      }
      deletedConversationIdsRef.current.add(conversation.id);
      setAllConversations((items) => items.filter((item) => item.id !== conversation.id));
      // Nao abrir outra conversa por conta propria: abrir dispara o marcar-como-lido
      // e uma conversa nao lida virava lida por causa de um delete em outra.
      if (deletingSelected) setSelectedId(null);
      await loadConversations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao apagar conversa');
    } finally {
      setLoading(false);
    }
  };

  // Close attach menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) setAttachMenu(false);
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const insertEmoji = (emoji: string) => {
    const el = draftTextareaRef.current;
    if (!el) { handleDraftChange(draft + emoji); return; }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    handleDraftChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const loadMediaLibrary = async () => {
    setMediaLibraryLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/media-library', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMediaLibrary(data.items || []);
    } catch {
      toast.error('Erro ao carregar biblioteca de mídias');
    } finally {
      setMediaLibraryLoading(false);
    }
  };

  const openMediaPicker = async () => {
    setAttachMenu(false);
    setMediaPickerOpen(true);
    setMediaSearch('');
    setMediaTypeFilter('all');
    loadMediaLibrary();
  };

  const selectFromLibrary = (item: MediaLibraryItem) => {
    const mimeType = item.mimeType || '';
    const type = item.type || (mimeType.startsWith('image/') ? 'image' : mimeType.startsWith('audio/') ? 'audio' : mimeType.startsWith('video/') ? 'video' : 'document');
    setAttachment({ url: item.url, type, contentType: mimeType, filename: item.name || item.originalName || type });
    setMediaPickerOpen(false);
  };

  const inferQuickReplyContentType = (step: QuickReplyStep) => {
    const url = String(step.content || '').split('?')[0].toLowerCase();
    if (step.type === 'image') {
      if (url.endsWith('.png')) return 'image/png';
      if (url.endsWith('.webp')) return 'image/webp';
      if (url.endsWith('.gif')) return 'image/gif';
      return 'image/jpeg';
    }
    if (step.type === 'audio') {
      if (url.endsWith('.mp3')) return 'audio/mpeg';
      if (url.endsWith('.m4a')) return 'audio/mp4';
      if (url.endsWith('.wav')) return 'audio/wav';
      return 'audio/ogg; codecs=opus';
    }
    if (step.type === 'video') return 'video/mp4';
    if (step.type === 'document') return 'application/octet-stream';
    return 'text/plain';
  };

  // Quick Reply logic
  const qrAllRef = useRef<QuickReplyItem[]>([]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    fetch('/api/quick-replies', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) qrAllRef.current = data; })
      .catch(() => undefined);
  }, []);

  const handleDraftChange = (value: string) => {
    setDraft(value);
    // Clear preview if user edits away from /name
    if (quickReplyPreview && !value.startsWith('/')) {
      setQuickReplyPreview(null);
      setQuickReplySuggestions([]);
      return;
    }
    if (!value.startsWith('/')) { setQuickReplySuggestions([]); return; }
    const query = value.slice(1).toLowerCase();
    if (!query) {
      setQuickReplySuggestions(qrAllRef.current.slice(0, 8));
      return;
    }
    const filtered = qrAllRef.current.filter((r) => r.name.includes(query));
    setQuickReplySuggestions(filtered.slice(0, 8));
    // If exact match, set preview
    const exact = qrAllRef.current.find((r) => r.name === query);
    if (exact) setQuickReplyPreview(exact);
    else setQuickReplyPreview(null);
  };

  const selectQuickReply = (item: QuickReplyItem) => {
    setDraft(`/${item.name}`);
    setQuickReplySuggestions([]);
    setQuickReplyPreview(item);
  };

  const dismissQuickReply = () => {
    setQuickReplyPreview(null);
    setQuickReplySuggestions([]);
    setDraft('');
  };

  const STEP_TYPE_ICONS: Record<string, string> = { text: '💬', image: '🖼️', audio: '🎵', video: '🎬', document: '📄' };

  const fireQuickReply = async () => {
    if (!quickReplyPreview || !selectedId || quickReplyFiring || isShopeeReplyDisabled) return;
    setQuickReplyFiring(true);
    setQuickReplyPreview(null);
    setQuickReplySuggestions([]);
    setDraft('');
    try {
      for (const step of quickReplyPreview.steps) {
        if (step.delayMs > 0) await new Promise((res) => setTimeout(res, step.delayMs));
        const body = step.type === 'text' ? (step.content || '') : (step.caption || '');
        const attachments = step.type !== 'text' && step.content
          ? [{ url: step.content, type: step.type, contentType: inferQuickReplyContentType(step), filename: step.content.split('/').pop()?.split('?')[0] || step.type }]
          : [];
        if (!body && !attachments.length) continue;
        await sendPayloadDirect(body, attachments.length ? attachments[0] as any : null);
      }
    } catch {
      toast.error('Erro ao disparar resposta rápida');
    } finally {
      setQuickReplyFiring(false);
    }
  };

  // ===========================================================================
  // TEMPLATES DA API OFICIAL — atalho "#" no compositor
  //
  // Fora da janela de 24h o WhatsApp só entrega template aprovado. Antes disso
  // significava sair do chat e ir para o Disparador/Templates; aqui o operador
  // digita "#", escolhe o template da PRÓPRIA conta daquela conversa, preenche
  // as variáveis e dispara sem perder o contexto.
  // ===========================================================================
  const templateQuery = draft.startsWith('#') ? draft.slice(1).toLowerCase().trim() : null;
  const templatesLoadedForRef = useRef<string | null>(null);

  const loadOfficialTemplates = useCallback(async (conversationId: string) => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const data = await apiRequest<OfficialTemplate[]>(`/conversations/${conversationId}/templates`);
      setOfficialTemplates(Array.isArray(data) ? data : []);
      templatesLoadedForRef.current = conversationId;
    } catch (error) {
      setOfficialTemplates([]);
      templatesLoadedForRef.current = null;
      setTemplatesError(error instanceof Error ? error.message : 'Erro ao carregar templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Carrega sob demanda, na primeira vez que o operador digita "#" naquela
  // conversa: a listagem bate na Zernio e não vale gastar a chamada em toda
  // conversa aberta.
  useEffect(() => {
    if (templateQuery === null || !supportsOfficialTemplates || !selectedId) return;
    if (templatesLoadedForRef.current === selectedId || templatesLoading) return;
    loadOfficialTemplates(selectedId);
  }, [templateQuery, supportsOfficialTemplates, selectedId, templatesLoading, loadOfficialTemplates]);

  // Trocou de conversa: o catálogo é por conta, então o anterior não serve — e
  // um modal aberto pertencia à conversa anterior (variáveis preenchidas com o
  // contato de lá).
  useEffect(() => {
    if (templatesLoadedForRef.current && templatesLoadedForRef.current !== selectedId) {
      templatesLoadedForRef.current = null;
      setOfficialTemplates([]);
      setTemplatesError(null);
    }
    setTemplateModal((current) => (current && current.conversationId !== selectedId ? null : current));
  }, [selectedId]);

  const templateSuggestions = useMemo(() => {
    if (templateQuery === null || !supportsOfficialTemplates) return [];
    if (!templateQuery) return officialTemplates.slice(0, 8);
    return officialTemplates
      .filter((item) => item.name.toLowerCase().includes(templateQuery) || String(item.category || '').toLowerCase().includes(templateQuery))
      .slice(0, 8);
  }, [templateQuery, supportsOfficialTemplates, officialTemplates]);

  // Valor inicial de cada variável: texto fixo do mapeamento sai pronto e as
  // variáveis de contato vêm preenchidas — o operador só confere.
  //
  // As chaves são as mesmas de CONTACT_TEMPLATE_VARIABLES (campaignController) e
  // do mapeamento da tela de Templates. "link" não é campo do contato: no
  // mapeamento ele vira texto fixo, então cai no ramo do prefixo `text:`.
  const prefillTemplateParams = (template: OfficialTemplate, conversation: Conversation, contact: ContactDetails | null) => {
    const contactName = (contact?.nome || conversation.contactName || '').trim();
    const firstName = contactName.split(/\s+/)[0] || '';
    return template.positions.map((_position, index) => {
      const entry = template.variableOrder?.[index];
      if (typeof entry === 'string' && entry.startsWith(TEMPLATE_TEXT_PREFIX)) return entry.slice(TEMPLATE_TEXT_PREFIX.length);
      const key = String(entry || '').toLowerCase();
      if (key === 'nome') return contactName;
      if (key === 'primeironome') return firstName;
      if (key === 'telefone') return contact?.telefone || conversation.contactPhone || '';
      if (key === 'email') return contact?.email || '';
      if (key === 'observacoes') return contact?.observacoes || '';
      if (key === 'categoria') return contact?.categoria?.nome || conversation.contactCategoria?.nome || '';
      return '';
    });
  };

  const selectTemplate = async (template: OfficialTemplate) => {
    const conversation = selected;
    if (!conversation) return;

    // `contactDetails` é um cache da PÁGINA: só o painel lateral o preenche e ele
    // NÃO é limpo ao trocar de conversa. Usá-lo sem conferir de quem é colocava o
    // nome do contato da conversa anterior no template desta. Só serve quando o
    // id bate com o contato desta conversa.
    let contact = contactDetails && conversation.contactId && contactDetails.id === conversation.contactId
      ? contactDetails
      : null;

    // O cadastro completo só é carregado quando o painel lateral é aberto — sem
    // buscar aqui, e-mail/observações/categoria sairiam vazios. Só vale a chamada
    // se o template realmente usa variável de sistema.
    const needsContact = template.positions.some((_position, index) => {
      const entry = template.variableOrder?.[index];
      return typeof entry === 'string' && entry.trim() !== '' && !entry.startsWith(TEMPLATE_TEXT_PREFIX);
    });
    if (needsContact && !contact && conversation.contactId) {
      try {
        const fetched = await contactRequest<ContactDetails>(`/${conversation.contactId}`);
        // Trocou de conversa enquanto buscava: descarta, senão volta o mesmo bug.
        if (selectedIdRef.current !== conversation.id) return;
        contact = fetched;
        setContactDetails(fetched);
      } catch {
        // Sem o cadastro o operador preenche na mão — não é motivo para travar.
      }
    }

    setTemplateModal({
      conversationId: conversation.id,
      template,
      params: prefillTemplateParams(template, conversation, contact),
      headerMedia: ''
    });
  };

  const dismissTemplateSuggestions = () => {
    if (draft.startsWith('#')) setDraft('');
  };

  // Mesma renderização do backend (renderOfficialTemplatePreview): o que aparece
  // aqui é o que vai ficar na bolha da conversa depois do envio.
  const renderTemplatePreview = (template: OfficialTemplate, params: string[]) => {
    const list = Array.isArray(template.components) ? template.components : [];
    const valueOf = (key: string) => {
      const index = template.positions.findIndex((position) => position.key === key);
      return index >= 0 ? (params[index] || '') : '';
    };
    const fill = (text: string | undefined, section: string) =>
      String(text || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (_match, slot) => valueOf(`${section}:${slot}`));
    const typeOf = (component: TemplateComponent) => String(component?.type || '').toLowerCase();

    const blocks: string[] = [];
    const header = list.find((component) => typeOf(component) === 'header');
    if (header && String(header.format || 'text').toLowerCase() === 'text') {
      const headerText = fill(header.text, 'header').trim();
      if (headerText) blocks.push(headerText);
    }
    const body = list.find((component) => typeOf(component) === 'body');
    if (body) {
      const bodyText = fill(body.text, 'body').trim();
      if (bodyText) blocks.push(bodyText);
    }
    const footer = list.find((component) => typeOf(component) === 'footer');
    if (footer && String(footer.text || '').trim()) blocks.push(String(footer.text).trim());
    const buttons = list
      .filter((component) => typeOf(component) === 'buttons')
      .flatMap((component) => (Array.isArray(component.buttons) ? component.buttons : []))
      .map((button) => String(button?.text || '').trim())
      .filter(Boolean);
    if (buttons.length) blocks.push(buttons.map((label) => `[ ${label} ]`).join('  '));
    return blocks.join('\n\n');
  };

  const isMediaHeaderTemplate = (template: OfficialTemplate | null) =>
    Boolean(template?.headerFormat && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.headerFormat));

  const fireTemplate = async () => {
    if (!templateModal || templateSending) return;
    // O envio vai para a conversa em que o modal foi ABERTO, nunca para "a que
    // estiver selecionada agora".
    const { conversationId, template, params, headerMedia } = templateModal;
    const missing = template.positions.findIndex((_position, index) => !String(params[index] || '').trim());
    if (missing >= 0) {
      toast.error(`Preencha "${template.positions[missing].label}" — a Meta recusa template com variável vazia.`);
      return;
    }
    setTemplateSending(true);
    try {
      const sent = await apiRequest<Message>(`/conversations/${conversationId}/send-template`, {
        method: 'POST',
        body: JSON.stringify({
          templateName: template.name,
          language: template.language,
          params: params.map((value) => value.trim()),
          headerMedia: headerMedia.trim() || undefined
        })
      });
      forceScrollToBottomRef.current = true;
      if (conversationId === selectedIdRef.current && sent) {
        setMessages((current) => (current.some((message) => message.id === sent.id) ? current : [...current, sent]));
      }
      setTemplateModal(null);
      if (conversationId === selectedIdRef.current) setDraft('');
      toast.success('Template enviado');
      await loadConversations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao disparar template', { duration: 8000 });
      // O backend registra a recusa como mensagem "failed" na conversa.
      loadMessages(conversationId).catch(() => undefined);
    } finally {
      setTemplateSending(false);
    }
  };

  const openAiAssist = () => {
    if (isShopeeReplyDisabled) return;
    setAiAssist({ open: true, tab: 'reply', rewriteMode: 'friendly', loading: false, result: '' });
  };

  const openSchedule = () => {
    if (isShopeeReplyDisabled) return;
    const next = new Date(Date.now() + 10 * 60 * 1000);
    next.setSeconds(0, 0);
    setScheduleAt(next.toISOString().slice(0, 16));
    setScheduleQuickReplyId(quickReplyPreview?.id || '');
    setEditingScheduleId(null);
    setScheduleOpen(true);
  };

  const editSchedule = (item: ScheduledMessage) => {
    setEditingScheduleId(item.id);
    setScheduleAt(new Date(item.scheduledFor).toISOString().slice(0, 16));
    setScheduleQuickReplyId(item.quickReplyId || '');
    setDraft(item.body || '');
    setAttachment(Array.isArray(item.attachments) && item.attachments[0] ? item.attachments[0] : null);
    setScheduleListOpen(false);
    setScheduleOpen(true);
  };

  const createSchedule = async () => {
    if (!selectedId || !scheduleAt || isShopeeReplyDisabled) return;
    setScheduling(true);
    try {
      const payload = scheduleQuickReplyId
        ? { quickReplyId: scheduleQuickReplyId, scheduledFor: new Date(scheduleAt).toISOString() }
        : { body: draft.trim(), attachments: attachment ? [attachment] : [], scheduledFor: new Date(scheduleAt).toISOString() };
      if (editingScheduleId) {
        await apiRequest(`/scheduled-messages/${editingScheduleId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiRequest(`/conversations/${selectedId}/scheduled-messages`, { method: 'POST', body: JSON.stringify(payload) });
      }
      toast.success('Mensagem agendada');
      setScheduleOpen(false);
      setEditingScheduleId(null);
      await loadScheduledMessages(selectedId);
      if (!scheduleQuickReplyId) { setDraft(''); setAttachment(null); }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao agendar mensagem');
    } finally {
      setScheduling(false);
    }
  };

  const cancelSchedule = async (item: ScheduledMessage) => {
    if (!window.confirm('Cancelar este agendamento?')) return;
    try {
      await apiRequest(`/scheduled-messages/${item.id}`, { method: 'DELETE' });
      if (selectedId) await loadScheduledMessages(selectedId);
      toast.success('Agendamento cancelado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao cancelar agendamento');
    }
  };

  const closeAiAssist = () => setAiAssist((prev) => ({ ...prev, open: false, loading: false, result: '' }));

  const runAiReply = async () => {
    if (!selectedId || !aiAssist.open) return;
    setAiAssist((prev) => prev.open ? { ...prev, loading: true, result: '' } : prev);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/ai-assist/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversationId: selectedId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Erro ao gerar resposta');
      setAiAssist((prev) => prev.open ? { ...prev, loading: false, result: data.text || '' } : prev);
    } catch (err: any) {
      toast.error(err.message);
      setAiAssist((prev) => prev.open ? { ...prev, loading: false } : prev);
    }
  };

  const runAiRewrite = async () => {
    if (!aiAssist.open || aiAssist.tab !== 'rewrite') return;
    if (!draft.trim()) { toast.error('Digite algo no campo de mensagem primeiro'); return; }
    setAiAssist((prev) => prev.open ? { ...prev, loading: true, result: '' } : prev);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/ai-assist/rewrite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ draftText: draft, mode: aiAssist.rewriteMode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Erro ao reescrever');
      setAiAssist((prev) => prev.open ? { ...prev, loading: false, result: data.text || '' } : prev);
    } catch (err: any) {
      toast.error(err.message);
      setAiAssist((prev) => prev.open ? { ...prev, loading: false } : prev);
    }
  };

  const applyAiResult = () => {
    if (!aiAssist.open) return;
    const text = aiAssist.result;
    if (text) { setDraft(text); closeAiAssist(); }
  };

  const filteredCategories = categories.filter((category) => {
    const term = categorySearch.trim().toLowerCase();
    if (!term) return true;
    return category.nome.toLowerCase().includes(term) || (category.funnel?.nome || 'Sem funil').toLowerCase().includes(term);
  });

  const groupedCategories = filteredCategories.reduce<Record<string, { name: string; items: CategoryOption[] }>>((acc, category) => {
    const key = category.funnelId || 'sem-funil';
    if (!acc[key]) acc[key] = { name: category.funnel?.nome || 'Sem funil', items: [] };
    acc[key].items.push(category);
    return acc;
  }, {});

  const renderCategoryOptions = () => (
    <>
      <option value="">Sem categoria</option>
      {Object.entries(groupedCategories).map(([funnelId, group]) => (
        <optgroup key={funnelId} label={group.name}>
          {group.items.map((category) => <option key={category.id} value={category.id}>{category.nome}</option>)}
        </optgroup>
      ))}
    </>
  );
  const [addingSharedContact, setAddingSharedContact] = useState<string | null>(null);
  // Cria um contato do CRM a partir de um cartao de contato (vCard) recebido no chat
  const addSharedContactToCrm = async (name: string | null | undefined, phone: string) => {
    setAddingSharedContact(phone);
    try {
      await contactRequest('', {
        method: 'POST',
        body: JSON.stringify({ nome: (name || '').trim() || phone, telefone: phone })
      });
      toast.success(`Contato ${name || phone} adicionado ao CRM`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao adicionar contato ao CRM');
    } finally {
      setAddingSharedContact(null);
    }
  };

  const renderAttachment = (item: ChatAttachment, index: number) => {
    if (item.type === 'contact_card') {
      return (
        <div key={index} className="mt-2 w-64 max-w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-800 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-base">👤</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{item.name || 'Contato'}</p>
              {item.org && <p className="truncate text-xs text-slate-500">{item.org}</p>}
              {(item.phones?.length ?? 0) === 0 && <p className="text-xs text-slate-400">Sem telefone</p>}
            </div>
          </div>
          {(item.phones || []).map((phone) => (
            <div key={phone} className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
              <span className="truncate text-xs text-slate-600">{phone}</span>
              <button
                type="button"
                onClick={() => addSharedContactToCrm(item.name, phone)}
                disabled={addingSharedContact === phone}
                className="flex-shrink-0 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {addingSharedContact === phone ? 'Adicionando...' : '+ Adicionar ao CRM'}
              </button>
            </div>
          ))}
        </div>
      );
    }
    if (item.type === 'location' && item.latitude !== undefined && item.longitude !== undefined) {
      const mapsUrl = item.url || `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
      return (
        <a
          key={index}
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 flex w-64 max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-slate-800 shadow-sm transition hover:bg-slate-50"
        >
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-base">📍</span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{item.name || 'Localização'}</span>
            {item.address && <span className="block truncate text-xs text-slate-500">{item.address}</span>}
            <span className="block text-xs text-blue-600">Abrir no mapa</span>
          </span>
        </a>
      );
    }
    const source = `${item.contentType || ''} ${item.type || ''} ${item.filename || ''} ${item.url || ''}`.toLowerCase();
    const label = item.filename || item.type || 'Anexo';
    if (!item.url) return null;
    if (source.includes('image') || source.includes('sticker') || /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(item.url)) {
      return <button key={index} onClick={() => setPreview(item)} className="mt-2 block max-w-full"><img src={item.url} alt={label} className="max-h-64 rounded-md object-contain" /></button>;
    }
    if (source.includes('audio') || source.includes('voice') || source.includes('ptt') || /\.(mp3|wav|ogg|oga|m4a|aac|opus)(\?|$)/i.test(item.url)) {
      return <audio key={index} src={item.url} controls className="mt-2 w-72 max-w-full" />;
    }
    if (source.includes('video') || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(item.url)) {
      return <button key={index} onClick={() => setPreview(item)} className="mt-2 block max-w-full"><video src={item.url} className="max-h-72 w-80 max-w-full rounded-md" /></button>;
    }
    return <a key={index} href={item.url} target="_blank" rel="noreferrer" className="block mt-2 underline text-xs">{label}</a>;
  };

  return (
    <div className="h-[calc(100vh-44px)] h-[calc(100dvh-44px)] bg-slate-50 flex flex-col overflow-hidden">
      {creditConfirmModal}
      <VoiceCloneManagerModal
        open={voiceManagerOpen}
        onClose={() => setVoiceManagerOpen(false)}
        onChanged={setVoiceClones}
      />
      <VoiceMessageModal
        open={voiceMessageOpen}
        initialText={draft}
        voices={voiceClones}
        onClose={() => setVoiceMessageOpen(false)}
        onSend={sendVoiceAudio}
        onManageVoices={() => { setVoiceMessageOpen(false); setVoiceManagerOpen(true); }}
      />
      <style>{`@keyframes crm-chat-vibrate { 0%, 100% { transform: translateX(0); } 20% { transform: translateX(-1px); } 40% { transform: translateX(1px); } 60% { transform: translateX(-1px); } 80% { transform: translateX(1px); } }`}</style>
      {pendingSelectId && (
        <div className="flex items-center justify-center gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
          Abrindo conversa...
        </div>
      )}
      <div className="border-b border-slate-200 bg-white px-3 py-3 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Chat CRM</h1>
            <p className="text-xs text-slate-500">Caixas de entrada por conexão</p>
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <button
              onClick={() => setVoiceManagerOpen(true)}
              title="Clone de Voz — vozes clonadas desta empresa"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100"
            >
              🎙️ Clone de Voz
              {voiceClones.length > 0 && (
                <span className="rounded-full bg-violet-600 px-1.5 text-[10px] font-bold text-white">{voiceClones.length}</span>
              )}
            </button>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                // Esvaziar o campo tambem "aplica": volta a listagem sem busca.
                if (!event.target.value.trim()) setAppliedSearch('');
              }}
              onKeyDown={(event) => { if (event.key === 'Enter') setAppliedSearch(search.trim()); }}
              className="w-full sm:w-72 rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Buscar conversa"
            />
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(() => {
            const totalUnread = Object.values(unreadByInbox).reduce((s, n) => s + n, 0);
            return (
              <>
                <button
                  onClick={() => setSelectedInboxKey('all')}
                  className={`relative flex items-center gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap ${selectedInboxKey === 'all' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  <span className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-slate-100 font-semibold">T</span>
                  Todas as conversas
                  {totalUnread > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white leading-none">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </button>
                {inboxes.map((inbox) => {
                  const key = `${inbox.provider}:${inbox.externalAccountId}`;
                  const inboxAiEnabled = isInboxAiEnabled(inbox);
                  const inboxUnread = unreadByInbox[key] || 0;
                  return (
                    <div
                      key={inbox.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedInboxKey(key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') setSelectedInboxKey(key);
                      }}
                      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap ${selectedInboxKey === key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                    >
                      <ConnectionIcon platform={inbox.platform} provider={inbox.provider} size="sm" />
                      <span className="max-w-48 truncate">{connectionName(inbox)}</span>
                      <WahaHealthAlert health={inbox.connectionHealth} compact />
                      {inboxUnread > 0 && (
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white leading-none">
                          {inboxUnread > 99 ? '99+' : inboxUnread}
                        </span>
                      )}
                      {(inbox.platform?.toLowerCase() !== 'shopee' || chatCapabilities.shopeeChatSendEnabled) && (
                        <button
                          type="button"
                          disabled={savingInboxAiId === inbox.id}
                          onClick={(event) => toggleInboxAiFromCard(inbox, event)}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold transition ${inboxAiEnabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'} disabled:cursor-wait disabled:opacity-60`}
                          title={inboxAiEnabled ? 'Desativar IA desta caixa' : 'Ativar IA desta caixa'}
                        >
                          {inboxAiEnabled ? 'IA On' : 'IA Off'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <aside className={`${selectedId ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 md:flex-shrink-0 border-r border-slate-200 bg-white flex-col`}>
          <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
            {isWahaInbox && (
              <button
                type="button"
                onClick={() => { setNewConvQuery(''); setNewConvResults([]); setNewConvOpen(true); }}
                className="mb-2 flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nova conversa
              </button>
            )}
            <div className="mb-2 grid grid-cols-2 rounded-md border border-slate-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setAssignmentScope('mine')}
                className={`rounded px-2 py-1.5 text-xs font-semibold transition ${assignmentScope === 'mine' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Atribuidas a mim
              </button>
              <button
                type="button"
                onClick={() => setAssignmentScope('all')}
                className={`rounded px-2 py-1.5 text-xs font-semibold transition ${assignmentScope === 'all' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Todas
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  disabled={!visibleConversationIds.length}
                  onChange={toggleSelectAllVisible}
                  className="h-3.5 w-3.5 rounded border-slate-300"
                />
                Selecionar todas
              </label>
              <select
                value={channelTypeFilter}
                onChange={(e) => setChannelTypeFilter(e.target.value)}
                title="Filtrar por tipo de entrada"
                className="ml-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 focus:border-emerald-400 focus:outline-none"
              >
                <option value="all">Todos os tipos</option>
                {availableChannelTypes.map((type) => (
                  <option key={type} value={type}>{channelLabel(type)}</option>
                ))}
              </select>
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value === 'unread' ? 'unread' : 'recent')}
                title="Ordenar conversas"
                className={`rounded-md border px-2 py-1 text-xs font-medium focus:outline-none ${sortMode === 'unread' ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-300 bg-white text-slate-600 focus:border-emerald-400'}`}
              >
                <option value="recent">Mais recentes</option>
                <option value="unread">Nao lidas primeiro</option>
              </select>
              <span className="ml-auto text-[11px] text-slate-500">{selectedVisibleCount}/{conversations.length}</span>
            </div>
            {selectedVisibleCount > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" disabled={bulkActionLoading} onClick={() => runBulkConversationAction('delete')} className="rounded border border-red-200 bg-white px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-60">Apagar</button>
                <button type="button" disabled={bulkActionLoading} onClick={() => runBulkConversationAction('unread')} className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60">Marcar nao lidas</button>
                <button type="button" disabled={bulkActionLoading} onClick={() => runBulkConversationAction('read')} className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60">Marcar lidas</button>
                <button type="button" disabled={bulkActionLoading} onClick={() => runBulkConversationAction('pin')} className="rounded border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-60">Fixar</button>
                <button type="button" disabled={bulkActionLoading} onClick={() => runBulkConversationAction('unpin')} className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60">Desafixar</button>
              </div>
            )}
          </div>
          <div
            ref={listScrollRef}
            onScroll={(event) => { listScrollTopRef.current = event.currentTarget.scrollTop; }}
            className="flex-1 overflow-y-auto"
          >
            {conversations.length === 0 ? (
              // Sem resultado local a lista fica vazia e o botao "carregar mais"
              // (que vive no ramo de baixo) desaparece junto: sem esta saida o
              // operador conclui que a conversa nao existe.
              <div className="space-y-3 p-5 text-sm text-slate-500">
                <div>Nenhuma conversa encontrada{search.trim() ? ' entre as carregadas' : ''}.</div>
                {search.trim() && search.trim() !== appliedSearch.trim() && (
                  <button
                    type="button"
                    onClick={() => setAppliedSearch(search.trim())}
                    className="w-full rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                  >
                    Buscar "{search.trim()}" em todas as conversas
                  </button>
                )}
                {!search.trim() && hasMoreConversations && (
                  <button
                    type="button"
                    disabled={loadingMoreConversations}
                    onClick={loadMoreConversations}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {loadingMoreConversations ? 'Carregando...' : 'Carregar mais conversas'}
                  </button>
                )}
              </div>
            ) : <>
            {conversations.map((conversation) => {
              const connection = conversation.connection;
              const unread = (conversation.unreadCount ?? 0) > 0;
              const isSelected = selectedId === conversation.id;
              const isChecked = selectedConversationIds.has(conversation.id);
              const pinned = isConversationPinned(conversation);
              const menuOpen = convMenuId === conversation.id;
              const currentFunnelId = conversation.contactCategoria?.funnelId || conversation.contactCategoria?.funnel?.id || '';
              const currentFunnel = currentFunnelId ? chatFunnelById.get(currentFunnelId) || null : null;
              const currentFunnelCategories = currentFunnel?.categories || [];
              const pipelineSaving = pipelineSavingContactId === conversation.contactId;
              return (
                <div
                  key={conversation.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(conversation.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setSelectedId(conversation.id);
                  }}
                  className={`group relative w-full text-left pl-9 pr-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : pinned ? 'bg-amber-50/40' : unread ? 'bg-blue-50/40' : ''}`}
                >
                  {/* indicador lateral de nao-lida */}
                  {unread && !isSelected && (
                    <span className="absolute left-0 top-0 bottom-0 w-1 rounded-r bg-blue-500" />
                  )}
                  <span
                    role="checkbox"
                    aria-checked={isChecked}
                    tabIndex={0}
                    title="Selecionar conversa"
                    onClick={(event) => { event.stopPropagation(); toggleConversationSelection(conversation.id, !isChecked); }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); toggleConversationSelection(conversation.id, !isChecked); }
                    }}
                    className="absolute left-2 top-3 inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent bg-white/70 text-slate-500 transition hover:border-blue-200 hover:bg-blue-50"
                  >
                    <span className={`h-3.5 w-3.5 rounded border ${isChecked ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'}`}>
                      {isChecked && <span className="block text-center text-[9px] leading-3 text-white">✓</span>}
                    </span>
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    title="Apagar conversa"
                    aria-label="Apagar conversa"
                    onClick={(event) => deleteConversation(conversation, event)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') deleteConversation(conversation, event);
                    }}
                    className="absolute left-2 top-10 inline-flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-xs font-semibold text-slate-400 opacity-0 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 group-hover:opacity-100 group-focus-visible:opacity-100"
                  >
                    x
                  </span>
                  <div className="flex gap-3">
                    <ConversationAvatar conversation={conversation} connection={connection} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`flex items-center gap-1 text-sm truncate ${unread ? 'font-bold text-slate-900' : 'font-medium text-slate-900'}`}>
                          {pinned && <span className="flex-shrink-0 text-amber-500" title="Fixada">📌</span>}
                          <span className="truncate">{labelForConversation(conversation)}</span>
                          {assignedUserId(conversation) === user?.id ? (
                            <span
                              className="ml-1 inline-flex flex-shrink-0 items-center rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white shadow-sm shadow-emerald-200"
                              style={{ animation: 'crm-chat-vibrate 0.5s linear infinite' }}
                              title={assignedUserName(conversation) ? `Atribuida para ${assignedUserName(conversation)}` : 'Atribuida para voce'}
                            >
                              Responda
                            </span>
                          ) : assignedUserId(conversation) ? (
                            <span
                              className="ml-1 inline-flex max-w-[120px] flex-shrink-0 items-center truncate rounded bg-indigo-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white shadow-sm shadow-indigo-200"
                              style={{ animation: 'crm-chat-vibrate 0.5s linear infinite' }}
                              title={`Atribuida para ${assignedUserName(conversation) || 'outro usuario'}`}
                            >
                              {shortUserLabel(assignedUserName(conversation)) || 'Atribuida'}
                            </span>
                          ) : null}
                        </span>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <div className="flex items-center gap-1.5">
                            {unread && (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white leading-none">
                                {(conversation.unreadCount ?? 0) > 99 ? '99+' : conversation.unreadCount}
                              </span>
                            )}
                            <span className="text-[11px] text-slate-500">{formatTime(conversation.lastMessageAt)}</span>
                          </div>
                          {/* menu de acoes da conversa (sempre visivel, abaixo do horario) */}
                          <span className="relative">
                            <span
                              role="button"
                              tabIndex={0}
                              title="Mais acoes"
                              aria-label="Mais acoes"
                              onClick={(event) => { event.stopPropagation(); setConvMenuId(menuOpen ? null : conversation.id); }}
                              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.stopPropagation(); setConvMenuId(menuOpen ? null : conversation.id); } }}
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-md border transition ${menuOpen ? 'border-slate-300 bg-slate-200 text-slate-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
                            </span>
                            {menuOpen && (
                              <>
                                <span className="fixed inset-0 z-10" onClick={(event) => { event.stopPropagation(); setConvMenuId(null); }} />
                                <span className="absolute right-0 top-7 z-20 w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-xl">
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => toggleConversationPinned(conversation, event)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                  >
                                    <span className="text-sm">📌</span>{pinned ? 'Desafixar conversa' : 'Fixar conversa no topo'}
                                  </span>
                                  {unread ? (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(event) => markConversationReadFromMenu(conversation, event)}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <span className="text-sm">✓</span>Marcar como lida
                                    </span>
                                  ) : (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(event) => markConversationUnread(conversation, event)}
                                      className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                      <span className="text-sm">●</span>Marcar como nao lida
                                    </span>
                                  )}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="truncate">{connectionName(connection || conversation)}</span>
                        <span className="uppercase">{channelLabel(conversation.channelType)}</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                        <select
                          value={conversation.contactCategoriaId || ''}
                          disabled={!conversation.contactId || !currentFunnel || pipelineSaving}
                          onChange={(event) => moveConversationContactToCategory(conversation, event.target.value)}
                          title={conversation.contactId ? (currentFunnel ? `Categoria em ${currentFunnel.nome}` : 'Use o botao ao lado para escolher um funil') : 'Vincule esta conversa a um contato para alterar o funil'}
                          className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] text-slate-700 outline-none focus:border-blue-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                        >
                          <option value="">{conversation.contactId ? (currentFunnel ? 'Selecionar categoria' : 'Sem funil') : 'Contato nao vinculado'}</option>
                          {currentFunnelCategories.map((category) => (
                            <option key={category.id} value={category.id}>{category.nome}</option>
                          ))}
                          {conversation.contactCategoriaId && !currentFunnelCategories.some((category) => category.id === conversation.contactCategoriaId) && (
                            <option value={conversation.contactCategoriaId}>{conversation.contactCategoria?.nome || 'Categoria atual'}</option>
                          )}
                        </select>
                        <button
                          type="button"
                          disabled={!conversation.contactId || pipelineSaving}
                          onClick={(event) => {
                            event.stopPropagation();
                            setFunnelMoveModal({ conversation, funnelId: '', categoriaId: '' });
                          }}
                          title="Mover contato para outro funil"
                          aria-label="Mover contato para outro funil"
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <svg className={`h-3.5 w-3.5 ${pipelineSaving ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5" />
                          </svg>
                        </button>
                      </div>
                      {(conversation.contactTags?.length ?? 0) > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {conversation.contactTags!.map((tag) => {
                            const tagColor = labelColorMap.get(tag) || '#64748b';
                            return (
                              <span
                                key={tag}
                                title={tag}
                                className="inline-block max-w-[110px] truncate rounded-full px-1.5 py-[1px] text-[9px] font-semibold leading-[13px] text-white"
                                style={{ backgroundColor: tagColor }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <p className={`mt-1 text-xs truncate ${unread ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {conversation.lastMessage?.body || conversation.lastMessage?.messageType || 'Sem mensagens'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {hasMoreConversations && (
              <div className="p-3">
                <button
                  type="button"
                  onClick={loadMoreConversations}
                  disabled={loadingMoreConversations}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                >
                  {loadingMoreConversations ? 'Carregando...' : 'Carregar mais conversas'}
                </button>
              </div>
            )}
            </>}
          </div>
        </aside>

        <section className={`${selectedId ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
          <header className="h-16 border-b border-slate-200 bg-white px-3 sm:px-5 flex items-center justify-between">
            <div className="min-w-0 flex items-center gap-3">
              {selected && (
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="md:hidden -ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                  aria-label="Voltar para a lista"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {selected ? (
                <ConversationAvatar conversation={selected} connection={selectedConnection} />
              ) : (
                selectedConnection && <ConnectionIcon platform={selectedConnection.platform} provider={selectedConnection.provider} />
              )}
              <div className="min-w-0">
                {selected?.channelType === 'group' ? (
                  <button
                    type="button"
                    onClick={() => setGroupInfoOpen(true)}
                    className="block max-w-full truncate text-left text-base font-semibold text-slate-900 hover:text-blue-700"
                    title="Abrir informacoes do grupo"
                  >
                    {labelForConversation(selected)}
                  </button>
                ) : (
                  <h2 className="text-base font-semibold text-slate-900 truncate">{selected ? labelForConversation(selected) : 'Selecione uma conversa'}</h2>
                )}
                {selected && (
                  <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500">
                    <span className="truncate">{connectionName(selectedConnection || selected)} / {hasChatIntegration(selectedConnection || selected) ? channelLabel(selected.channelType) : 'Esse canal nao possui integracao de CHAT'}</span>
                    {socialHandleFromConversation(selected) && selected.platform !== 'whatsapp' && <span className="truncate">{socialHandleFromConversation(selected)}</span>}
                    {socialProfileUrl(selected) && <a href={socialProfileUrl(selected)} target="_blank" rel="noreferrer" className="shrink-0 text-blue-600 hover:underline">Perfil</a>}
                  </div>
                )}
                <WahaHealthAlert health={selectedConnectionHealth} compact className="mt-1" />
              </div>
            </div>
            {selected && (
              <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
                {scheduledMessages.length > 0 && (
                  <button
                    onClick={() => setScheduleListOpen(true)}
                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
                  >
                    {scheduledMessages.length} agendamento{scheduledMessages.length > 1 ? 's' : ''}
                  </button>
                )}
                {selected.channelType !== 'account' && (
                  <div className="flex flex-col items-stretch gap-1">
                    <button
                      type="button"
                      onClick={() => setMeetingModalOpen(true)}
                      title="Agendar uma reunião com este contato na agenda de um usuário do Flow"
                      className="rounded-md border border-amber-500 bg-amber-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-600"
                    >
                      📅 Agendar Reunião
                    </button>
                    {conversationMeetings.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setMeetingModalOpen(true)}
                        title={conversationMeetings.map((m) => `${m.title} · ${new Date(m.startAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · ${MEETING_STATUS_LABELS[m.status] || m.status}`).join('\n')}
                        className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-amber-800 hover:bg-amber-100"
                      >
                        {conversationMeetings.length === 1
                          ? `Reunião ${new Date(conversationMeetings[0].startAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                          : `${conversationMeetings.length} reuniões marcadas`}
                      </button>
                    )}
                  </div>
                )}
                {!isShopeeReplyDisabled && (() => {
                  const conversationAiEnabled = isEffectiveConversationAiEnabled(selected, selectedConnection);
                  const inboxAiEnabled = isInboxAiEnabled(selectedConnection);
                  const blocked = !conversationAiEnabled && !inboxAiEnabled;
                  return (
                    <div className="flex flex-col items-stretch gap-1">
                      <button
                        onClick={toggleConversationAi}
                        disabled={blocked}
                        title={blocked ? 'Para ativar a IA voce precisa ativar em Configuracoes de IA por caixa de entrada' : undefined}
                        className={`rounded-md border px-2.5 py-1 text-xs font-medium ${conversationAiEnabled ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700' : 'border-red-600 bg-red-600 text-white hover:bg-red-700'} ${blocked ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        {conversationAiEnabled ? 'IA Ativada' : 'IA Desativada'}
                      </button>
                      <button
                        type="button"
                        onClick={clearConversationAiHistory}
                        disabled={clearingAiHistory}
                        title="Apagar historico de IA desta conversa"
                        className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-medium leading-tight text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:cursor-wait disabled:opacity-60"
                      >
                        {clearingAiHistory ? 'apagando...' : 'apagar historico de IA'}
                      </button>
                    </div>
                  );
                })()}
                <div className="flex flex-col items-stretch gap-1">
                  <button
                    onClick={openContactPanel}
                    className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {selected.contactId ? 'Dados do contato' : 'Criar/vincular contato'}
                  </button>
                  <button
                    onClick={openAssignModal}
                    className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-emerald-800 hover:bg-emerald-100"
                  >
                    Atribuir Conversa
                  </button>
                </div>
              </div>
            )}
          </header>

          {selected?.channelType === 'comment' && (
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 text-xs text-slate-700">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{selected.platform === 'shopee' ? 'Comentario de avaliacao Shopee' : 'Comentario em post'}</span>
                    {textMeta(selected.metadata, 'postId') && <span className="rounded bg-white px-2 py-1 text-slate-500">Post: {textMeta(selected.metadata, 'postId')}</span>}
                    {textMeta(selected.metadata, 'itemId') && <span className="rounded bg-white px-2 py-1 text-slate-500">Produto: {textMeta(selected.metadata, 'itemId')}</span>}
                    {textMeta(selected.metadata, 'ratingStar') && <span className="rounded bg-white px-2 py-1 text-slate-500">Nota: {textMeta(selected.metadata, 'ratingStar')}/5</span>}
                    {textMeta(selected.metadata, 'commentId') && <span className="rounded bg-white px-2 py-1 text-slate-500">Comentario: {textMeta(selected.metadata, 'commentId')}</span>}
                  </div>
                  {selected.platform === 'shopee' && textMeta(selected.metadata, 'itemName') && (
                    <p className="max-w-5xl whitespace-pre-wrap break-words leading-relaxed text-slate-800">Produto: {textMeta(selected.metadata, 'itemName')}</p>
                  )}
                  {textMeta(selected.metadata, 'postTitle') && (
                    <p className="max-w-5xl whitespace-pre-wrap break-words leading-relaxed text-slate-800">{textMeta(selected.metadata, 'postTitle')}</p>
                  )}
                  {!textMeta(selected.metadata, 'postTitle') && selected.platform !== 'shopee' && <p className="text-slate-500">Caption do post nao veio no evento.</p>}
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  {textMeta(selected.metadata, 'productLink') ? (
                    <a href={textMeta(selected.metadata, 'productLink')} target="_blank" rel="noreferrer" className="rounded-md border border-blue-200 bg-white px-3 py-2 text-center font-medium text-blue-700 hover:bg-blue-50">Abrir produto</a>
                  ) : textMeta(selected.metadata, 'postUrl') ? (
                    <a href={textMeta(selected.metadata, 'postUrl')} target="_blank" rel="noreferrer" className="rounded-md border border-blue-200 bg-white px-3 py-2 text-center font-medium text-blue-700 hover:bg-blue-50">Abrir post</a>
                  ) : (
                    <span className="rounded-md border border-slate-200 bg-white px-3 py-2 text-slate-500">{selected.platform === 'shopee' ? 'Sem link do produto' : 'Sem link do post'}</span>
                  )}
                  {textMeta(selected.metadata, 'commentUrl') && <a href={textMeta(selected.metadata, 'commentUrl')} target="_blank" rel="noreferrer" className="rounded-md border border-blue-200 bg-white px-3 py-2 text-center font-medium text-blue-700 hover:bg-blue-50">Abrir comentario</a>}
                </div>
              </div>
              {(boolMeta(selected.metadata, 'isReply') || textMeta(selected.metadata, 'parentCommentId')) && (
                <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-slate-600">
                  Resposta a outro comentario{textMeta(selected.metadata, 'parentCommentAuthor') ? ` de ${textMeta(selected.metadata, 'parentCommentAuthor')}` : ''}{textMeta(selected.metadata, 'parentCommentId') ? ` (${textMeta(selected.metadata, 'parentCommentId')})` : ''}
                  {textMeta(selected.metadata, 'parentCommentText') && <span className="mt-1 block whitespace-pre-wrap break-words">{textMeta(selected.metadata, 'parentCommentText')}</span>}
                </div>
              )}
            </div>
          )}

          {selected?.channelType === 'group' && groupInfoOpen && (
            <div className="border-b border-slate-200 bg-white px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-900">{labelForConversation(selected)}</h3>
                  {textMeta(selected.metadata, 'personalizationOrderSn') && <p className="mt-1 text-xs font-medium text-orange-700">Personalização do pedido {textMeta(selected.metadata, 'personalizationOrderSn')}{boolMeta(selected.metadata, 'personalizationConflict') ? ' · Revisão humana necessária' : ''}</p>}
                  {textMeta(selected.metadata, 'personalizationOrderSn') && <button type="button" className="mt-1 text-xs font-medium text-slate-600 underline" onClick={async () => { try { await apiService.finishMarketplacePersonalization(selected.id); await loadConversations(); toast.success('Atendimento encerrado. Uma nova compra poderá ser vinculada na próxima mensagem.'); } catch (error) { toast.error(error instanceof Error ? error.message : 'Não foi possível encerrar'); } }}>Encerrar personalização deste pedido</button>}
                  {boolMeta(selected.metadata, 'personalizationPrepared') && <p className="mt-1 text-xs text-amber-700">Pedido {textMeta(selected.metadata, 'orderSn')} preparado · Aguardando o comprador iniciar o chat Shopee</p>}
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                    {textMeta(selected.metadata, 'sessionName') && <span>Sessao: {textMeta(selected.metadata, 'sessionName')}</span>}
                  </div>
                </div>
                <button onClick={() => setGroupInfoOpen(false)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">Fechar</button>
              </div>
              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Membros</div>
                {groupParticipantsFromConversation(selected).length ? (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {groupParticipantsFromConversation(selected).map((participant, index) => (
                      <div key={`${participant.jid || participant.phone || participant.name || index}`} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                        <div className="font-medium text-slate-800 truncate">{participant.name || normalizePhoneLabel(participant.phone) || phoneFromJid(participant.jid) || 'Participante'}</div>
                        <div className="mt-0.5 text-slate-500 truncate">{normalizePhoneLabel(participant.phone) || phoneFromJid(participant.jid) || participant.jid}</div>
                        {participant.isAdmin && <div className="mt-1 text-[10px] font-semibold text-blue-700">Admin</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Os membros ainda nao vieram no evento do WhatsApp. As proximas mensagens continuam mostrando o telefone do participante.</p>
                )}
              </div>
            </div>
          )}

          <div
            ref={messagesScrollRef}
            onScroll={(event) => {
              const el = event.currentTarget;
              followBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
            }}
            className="flex-1 overflow-y-auto p-5 space-y-3"
          >
            {visibleMessages.map((message) => {
              const failure = messageFailure(message);
              const isSending = message.status === 'sending';
              return (
              <div key={message.id} className={`flex ${message.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] sm:max-w-[70%] rounded-lg px-3 py-2 text-sm ${isSending ? 'bg-blue-500/80 text-white' : failure ? 'bg-red-50 border border-red-300 text-red-950' : message.direction === 'outgoing' ? (message.senderName === 'Enviado pelo Whatsapp' ? 'bg-emerald-100 border border-emerald-200 text-emerald-950' : 'bg-blue-600 text-white') : 'bg-white border border-slate-200 text-slate-900'}`}>
                  {(message.senderName || selected?.channelType === 'group') && <div className="text-[11px] opacity-70 mb-1">{selected?.channelType === 'group' && message.direction === 'incoming' ? groupSenderLabel(message) : message.senderName}</div>}
                  {message.replyToExternalId && <div className="mb-1 rounded bg-black/10 px-2 py-1 text-[11px] opacity-80">Resposta a: {message.replyToExternalId}</div>}
                  {message.body && <div className="whitespace-pre-wrap break-words">{formatMessageBody(message.body)}</div>}
                  {message.attachments?.map((item, index) => renderAttachment(item, index))}
                  {failure && (
                    <div className="mt-2 rounded border border-red-300 bg-red-100 px-2 py-1.5 text-[11px] font-medium text-red-800">
                      <span aria-hidden>⚠</span> Não entregue ao cliente: {failure}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] opacity-70 flex justify-between gap-3">
                    <span className={isSending ? 'flex items-center gap-1' : undefined}>
                      {isSending && <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-white/60 border-t-transparent" aria-hidden />}
                      {formatTime(message.sentAt)}{message.direction === 'outgoing' ? ` · ${deliveryLabel(message)}` : ''}
                    </span>
                    {message.direction === 'incoming' && <button onClick={() => setReplyTo(message)} disabled={isShopeeReplyDisabled} className="underline disabled:cursor-not-allowed disabled:opacity-40">Responder</button>}
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          <footer className="border-t border-slate-200 bg-white">
            {/* Janela de 24h das plataformas oficiais (Meta) */}
            {isWindowClosed && (
              <div className={`border-b px-4 py-2.5 text-xs ${sendWindow?.humanAgentFallback ? 'border-sky-200 bg-sky-50 text-sky-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                <div className="font-semibold">
                  {sendWindow?.humanAgentFallback
                    ? <><span aria-hidden>👤</span> Janela de 24h fechada — envio será marcado como atendimento humano</>
                    : <><span aria-hidden>⏰</span> Janela de 24h fechada — {officialPlatformName(sendWindow?.platform)} não entrega mensagem livre agora</>}
                </div>
                <div className="mt-0.5">
                  {sendWindow?.neverReplied
                    ? 'Este contato nunca respondeu nesta conversa.'
                    : `Última mensagem do contato em ${formatTime(sendWindow?.lastInboundAt || undefined)}.`}
                  {' '}
                  {sendWindow?.humanAgentFallback
                    ? `${officialPlatformName(sendWindow?.platform)} aceita resposta de atendente até 7 dias depois da última mensagem do contato (tag HUMAN_AGENT). Se a conta não tiver essa permissão aprovada na Meta, a mensagem volta marcada como não entregue aqui na conversa.`
                    : String(sendWindow?.platform || '').toLowerCase() === 'whatsapp'
                      ? 'Para reengajar, digite # aqui no campo de mensagem e escolha um template aprovado desta conta. Quando o cliente responder, a janela reabre por 24h.'
                      : 'É preciso aguardar uma nova mensagem do contato para poder responder.'}
                  {sendWindow?.enforced === false && !sendWindow?.humanAgentFallback && ' O bloqueio está desligado (CRM_OFFICIAL_WINDOW_BLOCK=false): o envio será tentado e pode não chegar.'}
                </div>
              </div>
            )}
            {sendWindow?.applies && sendWindow?.open && typeof sendWindow.minutesLeft === 'number' && sendWindow.minutesLeft <= 120 && (
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600">
                <span aria-hidden>⏳</span> A janela de 24h desta conversa fecha em {sendWindow.minutesLeft} min. Depois disso só template aprovado.
              </div>
            )}
            {/* Templates da API oficial — atalho "#" */}
            {templateQuery !== null && supportsOfficialTemplates && !templateModal && !isShopeeReplyDisabled && (
              <div className="max-h-72 overflow-y-auto border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between px-3 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Templates aprovados (API oficial)</span>
                  {templatesLoading && <span className="text-[10px] text-slate-400">carregando…</span>}
                </div>
                {templatesError && (
                  <div className="px-3 py-2 text-xs text-red-600">{templatesError}</div>
                )}
                {!templatesLoading && !templatesError && templateSuggestions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    {officialTemplates.length === 0
                      ? 'Nenhum template aprovado nesta conta. Crie em WhatsApp > Templates.'
                      : 'Nenhum template com esse nome.'}
                  </div>
                )}
                {templateSuggestions.map((item) => (
                  <button
                    key={`${item.name}:${item.language}`}
                    onClick={() => selectTemplate(item)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-emerald-50"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-xs font-bold text-emerald-700">#</span>
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-slate-800">{item.name}</span>
                      <span className="block truncate text-xs text-slate-500">
                        {item.language}
                        {item.category ? ` · ${item.category}` : ''}
                        {item.positions.length ? ` · ${item.positions.length} variável(is)` : ' · sem variáveis'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {templateQuery !== null && !supportsOfficialTemplates && (
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Template aprovado só existe no WhatsApp da API oficial. Esta conversa é de outro canal.
              </div>
            )}

            {/* Quick Reply suggestions dropdown */}
            {quickReplySuggestions.length > 0 && !quickReplyPreview && !isShopeeReplyDisabled && (
              <div className="border-b border-slate-200 bg-white">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Respostas Rápidas</div>
                {quickReplySuggestions.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => selectQuickReply(item)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-violet-50 transition-colors"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-100 text-violet-700 font-bold text-xs">⚡</span>
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-slate-800">/{item.name}</span>
                      {item.description && <span className="block text-xs text-slate-500 truncate">{item.description}</span>}
                    </div>
                    <div className="ml-auto flex shrink-0 gap-1">
                      {item.steps.slice(0, 4).map((s, i) => (
                        <span key={i} className="text-sm" title={s.type}>{STEP_TYPE_ICONS[s.type] || '💬'}</span>
                      ))}
                      {item.steps.length > 4 && <span className="text-xs text-slate-400">+{item.steps.length - 4}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Quick Reply flow preview */}
            {quickReplyPreview && !isShopeeReplyDisabled && (
              <div className="border-b border-violet-200 bg-violet-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-violet-800">⚡ /{quickReplyPreview.name}</span>
                      {quickReplyPreview.description && <span className="text-xs text-violet-600">— {quickReplyPreview.description}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {quickReplyPreview.steps.map((s, i) => (
                        <div key={i} className="flex items-center gap-1 rounded-full border border-violet-200 bg-white px-2 py-0.5 text-xs text-slate-600">
                          {i > 0 && s.delayMs > 0 && (
                            <span className="text-violet-400 text-[10px]">⏱{s.delayMs / 1000}s →</span>
                          )}
                          <span>{STEP_TYPE_ICONS[s.type] || '💬'}</span>
                          <span className="max-w-[120px] truncate">{s.type === 'text' ? (s.content || '—') : (s.caption || s.type)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={dismissQuickReply} className="shrink-0 rounded p-1 text-violet-400 hover:bg-violet-100" title="Cancelar">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>
              </div>
            )}

            <div className="p-4">
              <WahaHealthAlert health={selectedConnectionHealth} className="mb-3" />
              {isShopeeReplyDisabled && (
                <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
                  A shopee não Permite o envio de mensagens de plataformas externas, você pode receber mas não pode enviar mensagens.
                </div>
              )}
              {replyTo && (
                <div className="mb-2 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                  <span className="truncate">Respondendo: {replyTo.body || replyTo.messageType}</span>
                  <button onClick={() => setReplyTo(null)} className="text-slate-500 hover:text-slate-900">x</button>
                </div>
              )}
              {attachment && (
                <div className="mb-2 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className="block truncate">Anexo: {attachment.filename || attachment.type || 'arquivo'}</span>
                    {isAudioAttachment(attachment) && attachment.url && <audio src={attachment.url} controls className="mt-2 h-9 w-full max-w-md" />}
                  </div>
                  <button onClick={() => setAttachment(null)} className="text-slate-500 hover:text-slate-900">x</button>
                </div>
              )}
              <div className="flex gap-2">
                {!recording && (
                  <div className="relative" ref={attachMenuRef}>
                    <button
                      onClick={() => setAttachMenu((v) => !v)}
                      disabled={uploading || loading || isShopeeReplyDisabled}
                      className={`inline-flex h-11 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50`}
                    >
                      {uploading ? (
                        <><svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Anexando</>
                      ) : (
                        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>Anexo<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg></>
                      )}
                    </button>
                    {attachMenu && (
                      <div className="absolute bottom-full left-0 mb-1 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-20">
                        <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                          Upload do computador
                          <input type="file" className="hidden" disabled={uploading || sending} onChange={(event) => { const file = event.target.files?.[0]; event.currentTarget.value = ''; setAttachMenu(false); if (file) uploadAttachment(file); }} />
                        </label>
                        <button
                          onClick={openMediaPicker}
                          className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                          Base de mídias
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={recording ? discardRecording : startRecording}
                  disabled={uploading || sending || loading || isShopeeReplyDisabled}
                  title={recording ? 'Parar gravação' : 'Gravar áudio'}
                  aria-label={recording ? 'Parar gravação' : 'Gravar áudio'}
                  className={`inline-flex h-11 min-w-11 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium disabled:opacity-50 ${recording ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  <MicrophoneIcon active={recording} />
                </button>
                {!recording && (
                  <div className="relative" ref={emojiRef}>
                    <button
                      onClick={() => setEmojiOpen((v) => !v)}
                      disabled={isShopeeReplyDisabled}
                      title="Emojis"
                      aria-label="Emojis"
                      className="inline-flex h-11 min-w-11 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-xl text-slate-600 hover:bg-slate-50"
                    >
                      😊
                    </button>
                    {emojiOpen && <EmojiPicker category={emojiCategory} onCategoryChange={setEmojiCategory} onPick={(e) => { insertEmoji(e); }} />}
                  </div>
                )}
                {!recording && (
                  <button
                    onClick={openAiAssist}
                    disabled={!selectedId || isShopeeReplyDisabled}
                    title="Assistente IA — Responder ou reescrever com IA"
                    aria-label="Assistente IA"
                    className="inline-flex h-11 min-w-11 items-center justify-center rounded-md border border-violet-300 bg-violet-50 px-3 text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 2a10 10 0 1 1 0 20A10 10 0 0 1 12 2Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M8 12h8M12 8l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="18" cy="6" r="2.5" fill="currentColor" opacity=".7"/>
                    </svg>
                  </button>
                )}
                {!recording && (
                  <button
                    onClick={() => (voiceClones.some((voice) => voice.ready) ? setVoiceMessageOpen(true) : setVoiceManagerOpen(true))}
                    disabled={!selectedId || isShopeeReplyDisabled}
                    title="Enviar como voz clonada — transforma o texto digitado em áudio"
                    aria-label="Enviar como voz clonada"
                    className="inline-flex h-11 min-w-11 items-center justify-center rounded-md border border-emerald-300 bg-emerald-50 px-3 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path d="M11 5 6 9H3v6h3l5 4V5Z" />
                      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
                {!recording && (
                  <button
                    onClick={openSchedule}
                    disabled={isShopeeReplyDisabled || !selectedId || (!draft.trim() && !attachment && !quickReplyPreview)}
                    title="Agendar envio"
                    aria-label="Agendar envio"
                    className="inline-flex h-11 min-w-11 items-center justify-center rounded-md border border-amber-300 bg-amber-50 px-3 text-amber-700 hover:bg-amber-100 disabled:opacity-40"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                  </button>
                )}
                {recording ? (
                  <div className="flex min-h-11 flex-1 items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 text-red-700">
                    <span className="h-2 w-2 rounded-full bg-red-600" />
                    <RecordingWaveform samples={recordingSamples} />
                  </div>
                ) : (
                  <textarea
                    ref={draftTextareaRef}
                    value={draft}
                    disabled={isShopeeReplyDisabled}
                    onChange={(event) => handleDraftChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') { dismissQuickReply(); dismissTemplateSuggestions(); setEmojiOpen(false); return; }
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        // Com "#" aberto, Enter escolhe o primeiro template em vez
                        // de mandar "#nome" como mensagem de texto.
                        if (templateQuery !== null && supportsOfficialTemplates) {
                          if (templateSuggestions.length) selectTemplate(templateSuggestions[0]);
                          return;
                        }
                        if (quickReplyPreview) fireQuickReply();
                        else send();
                      }
                    }}
                    className="min-h-[44px] max-h-32 flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                    placeholder={isShopeeReplyDisabled
                      ? 'Plataforma não aceita envio de mensagens'
                      : supportsOfficialTemplates
                        ? 'Digite a mensagem, / para respostas rápidas ou # para templates'
                        : 'Digite a mensagem ou / para respostas rápidas'}
                  />
                )}
                {(() => {
                  // Com "#" no campo, o botão escolhe o template em vez de mandar
                  // "#nome" como texto solto para o cliente.
                  const templatePicking = templateQuery !== null && supportsOfficialTemplates;
                  return (
                    <button
                      onClick={templatePicking ? () => templateSuggestions[0] && selectTemplate(templateSuggestions[0]) : (quickReplyPreview ? fireQuickReply : send)}
                      disabled={isShopeeReplyDisabled || sending || uploading || quickReplyFiring || !selectedId
                        || (templatePicking ? templateSuggestions.length === 0 : (!recording && !draft.trim() && !attachment && !quickReplyPreview))}
                      className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${templatePicking ? 'bg-emerald-600 hover:bg-emerald-700' : quickReplyPreview ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600'}`}
                    >
                      {templatePicking ? '# Template' : quickReplyFiring ? 'Disparando...' : sending ? 'Enviando' : quickReplyPreview ? '⚡ Disparar' : 'Enviar'}
                    </button>
                  );
                })()}
              </div>
            </div>
          </footer>
        </section>
        {contactPanelOpen && (
          <>
            <div
              className="fixed inset-0 top-11 z-30 bg-black/40 xl:hidden"
              onClick={() => setContactPanelOpen(false)}
              aria-hidden="true"
            />
          <aside className="fixed inset-y-0 right-0 top-11 z-40 w-80 max-w-[85vw] shadow-xl xl:static xl:top-auto xl:z-auto xl:max-w-none xl:shadow-none border-l border-slate-200 bg-white flex flex-col">
            <div className="h-16 border-b border-slate-200 px-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Dados do contato</h3>
                <p className="text-xs text-slate-500">Cadastro do CRM</p>
              </div>
              <div className="flex items-center gap-2">
                {contactDetails && !contactEditing && <button onClick={() => setContactEditing(true)} className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50">Editar</button>}
                <button onClick={() => setContactPanelOpen(false)} className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50">x</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-sm">
              {contactLoading ? (
                <div className="text-slate-500">Carregando...</div>
              ) : !contactDetails && contactForm ? (
                <div className="space-y-4">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Contato nao vinculado</div>
                    <p className="mt-1 text-xs text-slate-500">Crie um contato novo a partir desta conversa ou vincule a um contato existente.</p>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs text-slate-500">Buscar contato existente</label>
                    <div className="flex gap-2">
                      <input value={contactSearch} onChange={(event) => setContactSearch(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && searchContacts()} className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Nome, telefone ou email" />
                      <button onClick={searchContacts} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">Buscar</button>
                    </div>
                    {contactResults.length > 0 && (
                      <div className="space-y-2">
                        {contactResults.map((contact) => (
                          <button key={contact.id} onClick={() => linkExistingContact(contact.id)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50">
                            <span className="block font-medium text-slate-900">{contact.nome}</span>
                            <span className="text-xs text-slate-500">{formatPhone(contact.telefone) || contact.telefone}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="border-t border-slate-200 pt-4 space-y-3">
                    <div className="text-sm font-semibold text-slate-900">Novo contato</div>
                    <input value={contactForm.nome} onChange={(event) => setContactForm({ ...contactForm, nome: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Nome" />
                    <input value={selected?.contactPhone || phoneFromWahaMetadata(selected?.metadata, selected?.externalThreadId) || ''} disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" placeholder="Telefone/identificador da conversa" />
                    <div className="grid grid-cols-1 gap-2">
                      {SOCIAL_CONTACT_FIELDS.map((field) => (
                        <input key={field.key} value={String(contactForm[field.key] || '')} onChange={(event) => setContactForm({ ...contactForm, [field.key]: event.target.value })} className="min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder={field.label} />
                      ))}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm">
                        <span className="text-slate-400">⌕</span>
                        <input value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} className="min-w-0 flex-1 border-0 bg-transparent outline-none" placeholder="Buscar categoria ou funil" />
                      </div>
                      <select value={contactForm.categoriaId} onChange={(event) => setContactForm({ ...contactForm, categoriaId: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                        {renderCategoryOptions()}
                      </select>
                    </div>
                    <ContactLabelSelector value={contactForm.tags} onChange={(tags) => setContactForm({ ...contactForm, tags })} />
                    <textarea value={contactForm.observacoes} onChange={(event) => setContactForm({ ...contactForm, observacoes: event.target.value })} rows={3} className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Observacoes" />
                    <textarea value={contactForm.resumoIA} onChange={(event) => setContactForm({ ...contactForm, resumoIA: event.target.value })} rows={4} className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Resumo IA" />
                    <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">Follow-up<input type="checkbox" checked={contactForm.followUpAtivo} onChange={(event) => setContactForm({ ...contactForm, followUpAtivo: event.target.checked })} /></label>
                    <button onClick={createContactFromChat} className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white">Criar e vincular contato</button>
                  </div>
                </div>
              ) : contactDetails && contactEditing && contactForm ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Nome</label>
                    <input value={contactForm.nome} onChange={(event) => setContactForm({ ...contactForm, nome: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Telefone</label>
                    <input value={formatPhone(contactDetails.telefone) || contactDetails.telefone} disabled className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Categoria</label>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm">
                        <span className="text-slate-400">⌕</span>
                        <input value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} className="min-w-0 flex-1 border-0 bg-transparent outline-none" placeholder="Buscar categoria ou funil" />
                      </div>
                      <select value={contactForm.categoriaId} onChange={(event) => setContactForm({ ...contactForm, categoriaId: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                        {renderCategoryOptions()}
                      </select>
                    </div>
                  </div>
                  <ContactLabelSelector value={contactForm.tags} onChange={(tags) => setContactForm({ ...contactForm, tags })} />
                  <div>
                    <label className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                      Follow-up
                      <input type="checkbox" checked={contactForm.followUpAtivo} onChange={(event) => setContactForm({ ...contactForm, followUpAtivo: event.target.checked })} />
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Email</label>
                    <input value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">CNPJ/CPF</label>
                    <input value={contactForm.cnpj} onChange={(event) => setContactForm({ ...contactForm, cnpj: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Endereco</label>
                    <input value={contactForm.endereco} onChange={(event) => setContactForm({ ...contactForm, endereco: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {SOCIAL_CONTACT_FIELDS.map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs text-slate-500 mb-1">{field.label}</label>
                        <input value={String(contactForm[field.key] || '')} onChange={(event) => setContactForm({ ...contactForm, [field.key]: event.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Observacoes</label>
                    <textarea value={contactForm.observacoes} onChange={(event) => setContactForm({ ...contactForm, observacoes: event.target.value })} rows={4} className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Resumo IA</label>
                    <textarea value={contactForm.resumoIA} onChange={(event) => setContactForm({ ...contactForm, resumoIA: event.target.value })} rows={5} className="w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => { setContactEditing(false); setContactForm(buildContactForm(contactDetails)); }} className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancelar</button>
                    <button onClick={saveContactPanel} disabled={contactSaving} className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{contactSaving ? 'Salvando' : 'Salvar'}</button>
                  </div>
                </div>
              ) : contactDetails ? (
                <div className="space-y-4">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{contactDetails.nome}</div>
                    <div className="text-slate-600">{formatPhone(contactDetails.telefone) || contactDetails.telefone}</div>
                  </div>
                  <div className="grid gap-3">
                    <div><span className="block text-xs text-slate-500">Categoria</span><span className="text-slate-900">{contactDetails.categoria?.nome || 'Sem categoria'}</span></div>
                    <div><span className="block text-xs text-slate-500">Email</span><span className="text-slate-900 break-words">{contactDetails.email || '-'}</span></div>
                    <div><span className="block text-xs text-slate-500">CNPJ/CPF</span><span className="text-slate-900">{contactDetails.cnpj || '-'}</span></div>
                    <div><span className="block text-xs text-slate-500">Endereco</span><span className="text-slate-900 break-words">{contactDetails.endereco || '-'}</span></div>
                    {SOCIAL_CONTACT_FIELDS.map((field) => (
                      <div key={field.key}><span className="block text-xs text-slate-500">{field.label}</span><span className="text-slate-900 break-words">{String(contactDetails[field.key as keyof ContactDetails] || '-')}</span></div>
                    ))}
                    <div><span className="block text-xs text-slate-500">Follow-up</span><span className="text-slate-900">{contactDetails.followUpAtivo ? 'Ativo' : 'Inativo'}</span></div>
                    <div><span className="block text-xs text-slate-500">Ultima mensagem</span><span className="text-slate-900">{contactDetails.ultimaMensagem ? formatTime(contactDetails.ultimaMensagem) : '-'}</span></div>
                    <div><span className="block text-xs text-slate-500">Ultimo follow-up</span><span className="text-slate-900">{contactDetails.ultimoFollowUpEnviadoEm ? formatTime(contactDetails.ultimoFollowUpEnviadoEm) : '-'}</span></div>
                  </div>
                  {contactDetails.tags && contactDetails.tags.length > 0 && (
                    <div>
                      <span className="block text-xs text-slate-500 mb-2">Tags</span>
                      <div className="flex flex-wrap gap-2">{contactDetails.tags.map((tag) => <span key={tag} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">{tag}</span>)}</div>
                    </div>
                  )}
                  <div>
                    <span className="block text-xs text-slate-500 mb-1">Observacoes</span>
                    <p className="whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-slate-700">{contactDetails.observacoes || 'Sem observacoes'}</p>
                  </div>
                  <div>
                    <span className="block text-xs text-slate-500 mb-1">Resumo IA</span>
                    <p className="whitespace-pre-wrap break-words rounded-md bg-slate-50 p-3 text-slate-700">{contactDetails.resumoIA || 'Sem resumo IA'}</p>
                  </div>
                </div>
              ) : (
                <div className="text-slate-500">Nenhum contato carregado.</div>
              )}
            </div>
          </aside>
          </>
        )}
      </div>

      {/* Modal: nova conversa (WhatsApp Nao Oficial) */}
      {newConvOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16 sm:pt-24" onClick={() => setNewConvOpen(false)}>
          <div className="w-full max-w-md max-h-[80dvh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900">Nova conversa</h3>
                <button type="button" onClick={() => setNewConvOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Fechar">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Busque um contato ou digite um número. Se já houver conversa, ela é reaberta.</p>
              <input
                autoFocus
                value={newConvQuery}
                onChange={(e) => setNewConvQuery(e.target.value)}
                placeholder="Nome ou número (ex.: 11987654321)"
                className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {(() => {
                const digits = newConvQuery.replace(/\D/g, '');
                const canDialNumber = digits.length >= 8;
                return (
                  <>
                    {canDialNumber && (
                      <button
                        type="button"
                        disabled={newConvStarting}
                        onClick={() => startNewConversation({ phone: digits })}
                        className="mb-1 flex w-full items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-left text-sm text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
                        </span>
                        <span className="min-w-0">
                          <span className="block font-semibold">Iniciar conversa por número</span>
                          <span className="block text-xs text-emerald-700">{digits}</span>
                        </span>
                      </button>
                    )}
                    {newConvSearching && <div className="px-3 py-3 text-xs text-slate-400">Buscando contatos...</div>}
                    {!newConvSearching && newConvQuery.trim().length >= 2 && newConvResults.length === 0 && (
                      <div className="px-3 py-3 text-xs text-slate-400">Nenhum contato encontrado.</div>
                    )}
                    {newConvResults.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        disabled={newConvStarting}
                        onClick={() => startNewConversation({ contactId: contact.id })}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                          {(contact.nome || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-slate-900">{contact.nome || 'Sem nome'}</span>
                          <span className="block truncate text-xs text-slate-500">{contact.telefone}</span>
                        </span>
                      </button>
                    ))}
                    {newConvQuery.trim().length < 2 && !canDialNumber && (
                      <div className="px-3 py-6 text-center text-xs text-slate-400">Digite ao menos 2 caracteres para buscar, ou um número para iniciar direto.</div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {funnelMoveModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => !pipelineSavingContactId && setFunnelMoveModal(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Mover contato para outro funil</h3>
              <p className="mt-1 truncate text-xs text-slate-500">{labelForConversation(funnelMoveModal.conversation)}</p>
            </div>
            <div className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-700">Funil de destino</label>
                <select
                  autoFocus
                  value={funnelMoveModal.funnelId}
                  onChange={(event) => setFunnelMoveModal({ ...funnelMoveModal, funnelId: event.target.value, categoriaId: '' })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
                >
                  <option value="">Selecione um funil</option>
                  {chatFunnels.map((funnel) => (
                    <option key={funnel.id} value={funnel.id}>{funnel.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-700">Categoria no novo funil</label>
                <select
                  value={funnelMoveModal.categoriaId}
                  disabled={!funnelMoveModal.funnelId}
                  onChange={(event) => setFunnelMoveModal({ ...funnelMoveModal, categoriaId: event.target.value })}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100"
                >
                  <option value="">Selecione uma categoria</option>
                  {funnelMoveCategories.map((category) => (
                    <option key={category.id} value={category.id}>{category.nome}</option>
                  ))}
                </select>
                {funnelMoveModal.funnelId && funnelMoveCategories.length === 0 && (
                  <p className="mt-1.5 text-xs text-amber-700">Este funil ainda nao possui categorias.</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                disabled={!!pipelineSavingContactId}
                onClick={() => setFunnelMoveModal(null)}
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={!funnelMoveModal.categoriaId || !!pipelineSavingContactId}
                onClick={() => moveConversationContactToCategory(funnelMoveModal.conversation, funnelMoveModal.categoriaId)}
                className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pipelineSavingContactId ? 'Movendo...' : 'Mover contato'}
              </button>
            </div>
          </div>
        </div>
      )}

      {assignModalOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssignModalOpen(false)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Atribuir conversa</h3>
                <p className="text-xs text-slate-500 truncate">{labelForConversation(selected)}</p>
              </div>
              <button onClick={() => setAssignModalOpen(false)} className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50">x</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {user && (
                <button
                  type="button"
                  disabled={assigningConversation}
                  onClick={() => assignConversationTo(user.id)}
                  className="mb-3 w-full rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-left text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                >
                  Atribuir para mim
                </button>
              )}
              {assignedUserId(selected) && (
                <button
                  type="button"
                  disabled={assigningConversation}
                  onClick={() => assignConversationTo(null)}
                  className="mb-3 w-full rounded-md border border-red-200 bg-white px-3 py-2 text-left text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                >
                  Remover atribuicao
                </button>
              )}
              <div className="space-y-2">
                {users.map((item) => {
                  const active = assignedUserId(selected) === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={assigningConversation}
                      onClick={() => assignConversationTo(item.id)}
                      className={`w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-60 ${active ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'}`}
                    >
                      <span className="block font-medium text-slate-900">{item.nome || item.email}</span>
                      <span className="text-xs text-slate-500">{item.email}</span>
                    </button>
                  );
                })}
                {!users.length && <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">Nenhum usuario carregado.</div>}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Media Library Picker Modal */}
      {mediaPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setMediaPickerOpen(false)}>
          <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Base de Mídias</h3>
                  <p className="text-xs text-slate-500">Selecione um arquivo para anexar</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={loadMediaLibrary} disabled={mediaLibraryLoading} title="Atualizar" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 disabled:opacity-40">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={mediaLibraryLoading ? 'animate-spin' : ''}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                </button>
                <button onClick={() => setMediaPickerOpen(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
                </button>
              </div>
            </div>

            {/* Search + filter */}
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
              <div className="relative flex-1 min-w-[160px]">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input
                  value={mediaSearch}
                  onChange={(e) => setMediaSearch(e.target.value)}
                  placeholder="Buscar por nome..."
                  className="w-full rounded-md border border-slate-200 py-1.5 pl-8 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:outline-none"
                />
              </div>
              {(['all', 'image', 'audio', 'video', 'document'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setMediaTypeFilter(t)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${mediaTypeFilter === t ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {t === 'all' ? 'Todos' : t === 'image' ? '🖼 Imagens' : t === 'audio' ? '🎵 Áudios' : t === 'video' ? '🎬 Vídeos' : '📄 Docs'}
                </button>
              ))}
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {mediaLibraryLoading ? (
                <div className="flex h-full items-center justify-center text-slate-400">
                  <svg className="animate-spin mr-2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                  Carregando...
                </div>
              ) : (() => {
                const filtered = mediaLibrary.filter((m) => {
                  const matchType = mediaTypeFilter === 'all' || m.type === mediaTypeFilter;
                  const term = mediaSearch.toLowerCase();
                  const matchName = !term || (m.name || m.originalName || '').toLowerCase().includes(term);
                  return matchType && matchName;
                });
                if (!filtered.length) return (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    {mediaLibrary.length === 0 ? 'Nenhuma mídia na biblioteca.' : 'Nenhuma mídia encontrada.'}
                  </div>
                );
                return (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {filtered.map((item) => {
                      const label = item.name || item.originalName || 'Sem nome';
                      const t = item.type || 'document';
                      return (
                        <button
                          key={item.id}
                          onClick={() => selectFromLibrary(item)}
                          className="group flex flex-col items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          {t === 'image' ? (
                            <img src={item.url} alt={label} className="h-20 w-full rounded object-cover group-hover:opacity-90" />
                          ) : (
                            <div className="flex h-20 w-full items-center justify-center rounded bg-slate-100 text-3xl">
                              {t === 'audio' ? '🎵' : t === 'video' ? '🎬' : '📄'}
                            </div>
                          )}
                          <span className="line-clamp-2 w-full text-[11px] text-slate-600 leading-tight">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {meetingModalOpen && selectedId && (
        <ScheduleMeetingModal
          conversationId={selectedId}
          onClose={() => {
            setMeetingModalOpen(false);
            if (selectedIdRef.current) {
              loadConversationMeetings(selectedIdRef.current).catch(() => undefined);
              loadScheduledMessages(selectedIdRef.current).catch(() => undefined);
            }
          }}
          onScheduled={() => {
            if (selectedIdRef.current) {
              loadConversationMeetings(selectedIdRef.current).catch(() => undefined);
              loadScheduledMessages(selectedIdRef.current).catch(() => undefined);
              loadMessages(selectedIdRef.current).catch(() => undefined);
            }
          }}
        />
      )}

      {scheduleListOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setScheduleListOpen(false)}>
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Agendamentos da conversa</h3>
              <p className="mt-0.5 text-xs text-slate-500">Clique em editar para alterar data, conteudo ou resposta rapida.</p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
              {scheduledMessages.map((item) => (
                <div key={item.id} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {(item.flowEventId || item.kind === 'template') && (
                        <span className="mb-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{item.flowEventId ? 'Lembrete de reunião' : 'Template'}{item.kind === 'template' && item.template?.name ? ` · #${item.template.name}` : ''}</span>
                      )}
                      <div className="font-medium text-slate-900">{item.quickReply ? `/${item.quickReply.name}` : (item.body || 'Mensagem com anexo')}</div>
                      <div className="mt-1 text-xs text-slate-500">{new Date(item.scheduledFor).toLocaleString('pt-BR')}</div>
                      {item.error && <div className="mt-1 text-xs text-red-600">Erro: {item.error}</div>}
                    </div>
                    <div className="flex gap-2">
                      {!item.flowEventId && item.kind !== 'template' && (
                        <button onClick={() => editSchedule(item)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-white">Editar</button>
                      )}
                      <button onClick={() => cancelSchedule(item)} className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50">Cancelar</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <button onClick={() => setScheduleListOpen(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assist Modal */}
      {/* Preenchimento das variáveis do template antes do disparo */}
      {templateModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => !templateSending && setTemplateModal(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900"># {templateModal.template.name}</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Template aprovado · {templateModal.template.language}
                {templateModal.template.category ? ` · ${templateModal.template.category}` : ''}
                {' · '}para {(() => {
                  // Sempre a conversa DO MODAL — nunca "a selecionada agora".
                  const target = allConversations.find((item) => item.id === templateModal.conversationId);
                  return target?.contactPhone || target?.contactName || 'este contato';
                })()}
              </p>
            </div>
            <div className="space-y-4 px-5 py-4">
              {(!templateModal.template.components || templateModal.template.components.length === 0) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  A conta não devolveu o conteúdo deste template, então não dá para saber se ele tem variáveis.
                  Abra WhatsApp &gt; Templates uma vez para sincronizar antes de disparar.
                </div>
              )}
              {templateModal.template.positions.length === 0 && (
                <p className="text-sm text-slate-600">Este template não tem variáveis: é só confirmar o envio.</p>
              )}
              {templateModal.template.positions.map((position, index) => (
                <div key={position.key}>
                  <label className="block text-xs font-medium text-slate-600">{position.label}</label>
                  <input
                    value={templateModal.params[index] || ''}
                    onChange={(event) => setTemplateModal((current) => {
                      if (!current) return current;
                      const params = [...current.params];
                      params[index] = event.target.value;
                      return { ...current, params };
                    })}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Valor que vai substituir a variável"
                  />
                </div>
              ))}

              {isMediaHeaderTemplate(templateModal.template) && (
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    Mídia do cabeçalho ({templateModal.template.headerFormat}) — URL https
                  </label>
                  <input
                    value={templateModal.headerMedia}
                    onChange={(event) => setTemplateModal((current) => (current ? { ...current, headerMedia: event.target.value } : current))}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Deixe vazio para usar a mídia aprovada no template"
                  />
                </div>
              )}

              <div>
                <span className="block text-xs font-medium text-slate-600">Pré-visualização</span>
                <div className="mt-1 whitespace-pre-wrap rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-slate-800">
                  {renderTemplatePreview(templateModal.template, templateModal.params) || 'Sem conteúdo de texto.'}
                </div>
              </div>

              <p className="text-xs text-slate-500">
                O template é o único envio que atravessa a janela de 24h. Ele é cobrado pela Meta como conversa iniciada pela empresa.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                onClick={() => setTemplateModal(null)}
                disabled={templateSending}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={fireTemplate}
                disabled={templateSending}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {templateSending ? 'Enviando…' : 'Disparar template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {scheduleOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setScheduleOpen(false)}>
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">{editingScheduleId ? 'Editar agendamento' : 'Agendar envio'}</h3>
              <p className="mt-0.5 text-xs text-slate-500">Vinculado a esta conversa.</p>
            </div>
            <div className="space-y-4 px-5 py-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Quando enviar</span>
                <input type="datetime-local" value={scheduleAt} onChange={(event) => setScheduleAt(event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Tipo</span>
                <select value={scheduleQuickReplyId} onChange={(event) => setScheduleQuickReplyId(event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Mensagem digitada/anexo atual</option>
                  {qrAllRef.current.map((item) => <option key={item.id} value={item.id}>/{item.name}</option>)}
                </select>
              </label>
              {!scheduleQuickReplyId && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <div className="font-medium text-slate-800">Conteudo que sera agendado</div>
                  <div className="mt-1 whitespace-pre-wrap break-words">{draft.trim() || 'Sem texto'}</div>
                  {attachment && <div className="mt-1 truncate">Anexo: {attachment.filename || attachment.type || 'arquivo'}</div>}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button onClick={() => setScheduleOpen(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancelar</button>
              <button onClick={createSchedule} disabled={scheduling || !scheduleAt || (!scheduleQuickReplyId && !draft.trim() && !attachment)} className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50">{scheduling ? 'Salvando' : editingScheduleId ? 'Salvar' : 'Agendar'}</button>
            </div>
          </div>
        </div>
      )}

      {aiAssist.open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={closeAiAssist}>
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2a10 10 0 1 1 0 20A10 10 0 0 1 12 2Z" stroke="currentColor" strokeWidth="1.5"/><path d="M8 12h8M12 8l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="18" cy="6" r="2.5" fill="currentColor" opacity=".7"/></svg>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Assistente IA</h3>
                  <p className="text-xs text-slate-500">Ajuda inteligente para sua resposta</p>
                </div>
              </div>
              <button onClick={closeAiAssist} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-200">
              {(['reply', 'rewrite'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setAiAssist((prev) => prev.open ? { ...prev, tab, result: '' } : prev)}
                  className={`flex-1 py-3 text-sm font-medium transition-colors ${aiAssist.tab === tab ? 'border-b-2 border-violet-600 text-violet-700' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {tab === 'reply' ? '💬 Responder com IA' : '✍️ Reescrever com IA'}
                </button>
              ))}
            </div>

            <div className="p-5 space-y-4">
              {aiAssist.tab === 'reply' && (
                <>
                  <p className="text-xs text-slate-500">A IA vai analisar a conversa e os dados da empresa para sugerir uma resposta curta e adequada.</p>
                  <CreditCostNotice items={[{ featureKey: 'crm_ai_assist', qty: 1 }]} note="Cobrado por sugestão gerada" />
                  <button
                    onClick={runAiReply}
                    disabled={aiAssist.loading}
                    className="w-full rounded-lg bg-violet-600 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {aiAssist.loading ? (
                      <><svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Gerando resposta...</>
                    ) : '✨ Gerar resposta'}
                  </button>
                </>
              )}

              {aiAssist.tab === 'rewrite' && (
                <>
                  <div>
                    <p className="text-xs text-slate-500 mb-3">Selecione o modo de reescrita:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {REWRITE_MODES.map((m) => (
                        <button
                          key={m.value}
                          onClick={() => setAiAssist((prev) => prev.open ? { ...prev, rewriteMode: m.value, result: '' } : prev)}
                          className={`rounded-lg border p-3 text-left transition-all ${aiAssist.rewriteMode === m.value ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-violet-300 hover:bg-slate-50'}`}
                        >
                          <span className="block text-lg mb-1">{m.icon}</span>
                          <span className={`block text-xs font-semibold ${aiAssist.rewriteMode === m.value ? 'text-violet-700' : 'text-slate-700'}`}>{m.label}</span>
                          <span className="block text-[10px] text-slate-500 mt-0.5 leading-tight">{m.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {!draft.trim() && (
                    <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">⚠️ Digite sua mensagem no campo de texto antes de reescrever.</p>
                  )}
                  <CreditCostNotice items={[{ featureKey: 'crm_ai_assist', qty: 1 }]} note="Cobrado por sugestão gerada" />
                  <button
                    onClick={runAiRewrite}
                    disabled={aiAssist.loading || !draft.trim()}
                    className="w-full rounded-lg bg-violet-600 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {aiAssist.loading ? (
                      <><svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Reescrevendo...</>
                    ) : `✨ Reescrever — ${REWRITE_MODES.find(m => m.value === aiAssist.rewriteMode)?.label}`}
                  </button>
                </>
              )}

              {/* Result area */}
              {aiAssist.result && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
                    <p className="text-xs font-semibold text-violet-700 mb-2">Sugestão da IA:</p>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{aiAssist.result}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={applyAiResult}
                      className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                    >
                      ✅ Usar esta resposta
                    </button>
                    <button
                      onClick={() => setAiAssist((prev) => prev.open ? { ...prev, result: '' } : prev)}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                    >
                      Refazer
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)}>
          <div className="max-h-full max-w-5xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex justify-end gap-2">
              <a href={preview.url} download={preview.filename || true} className="rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100">Baixar</a>
              <button onClick={() => setPreview(null)} className="rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-100">Fechar</button>
            </div>
            {`${preview.contentType || ''} ${preview.type || ''} ${preview.filename || ''} ${preview.url || ''}`.toLowerCase().includes('video') || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(preview.url || '') ? (
              <video src={preview.url} controls autoPlay className="max-h-[82vh] max-w-full rounded-md bg-black" />
            ) : (
              <img src={preview.url} alt={preview.filename || 'Imagem'} className="max-h-[82vh] max-w-full rounded-md object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
